<template>
  <div class="cave-system-container" :class="caveTheme === 'blue' ? 'cave-theme-blue' : 'cave-theme-classic'">
    <!-- 未拥有洞府：创建引导层 -->
    <div 
          v-if="!hasCave && !caveLoading && showCreatePanel" 
          class="create-cave-overlay"
          @click.self="closeCreatePanel"
        >
      <div class="create-cave-panel">
         <!-- 添加关闭按钮 -->
      <div class="panel-close-btn" @click="closeCreatePanel">
        <span class="cave-xian-glyph cave-xian-glyph-close">×</span>
      </div>
        <span class="cave-xian-glyph cave-xian-glyph-home">府</span>
        <h2>开辟洞府</h2>
        <p class="desc">消耗材料开辟属于自己的修真洞府，开启十二时辰修炼体系</p>

        <div class="material-checklist">
          <div
            v-for="(item, idx) in createRequirements"
            :key="idx"
            class="material-item"
            :class="{ 'sufficient': item.have >= item.need }"
          >
            <img :src="item.icon" v-if="item.icon" class="mat-icon">
            <div v-else class="mat-icon-placeholder">{{ item.name[0] }}</div>
            <div class="mat-info">
              <div class="mat-name">{{ item.name }}</div>
              <div class="mat-count" :class="{ 'enough': item.have >= item.need }">
                拥有: {{ item.have }} / 需要: {{ item.need }}
              </div>
            </div>
            <span class="cave-material-status" :class="{ enough: item.have >= item.need }">{{ item.have >= item.need ? '成' : '缺' }}</span>
          </div>
        </div>

        <div class="cave-name-input">
          <Input
            v-model="newCaveName"
            placeholder="请输入洞府名称（2-6个字符）"
            :maxlength="6"
            style="width: 300px"
          />
        </div>

        <Button
          type="primary"
          size="large"
          :loading="isCreating"
          :disabled="!canCreate"
          @click="handleCreateCave"
          style="width: 300px; margin-top: 20px;"
        >
          {{ isCreating ? '开辟中...' : '立即开辟' }}
        </Button>
        <p v-if="!canCreate" class="warning-text">材料不足，无法开辟洞府</p>
      </div>
    </div>
    <!-- 加载中 -->
    <!-- 关闭后显示的重新打开按钮（放在 container 内合适位置） -->
    <div 
      v-if="!hasCave && !caveLoading && !showCreatePanel" 
      class="reopen-create-btn"
      @click="openCreatePanel"
    >
      <Button type="primary" size="large">
        开辟洞府
      </Button>
    </div>
    <div v-if="caveLoading" class="loading-container">
      <Spin size="large" fix>
        <span class="cave-xian-glyph cave-xian-glyph-loading spin-icon-load">灵</span>
        <div>加载洞府数据中...</div>
      </Spin>
    </div>

    <div v-if="hasCave && !caveLoading" class="cave-farm-page-shell cave-farm-fullscreen">
      <section v-if="!currentFacility && !settingsVisible" class="cave-overview-screen" :style="{ backgroundImage: 'url(' + caveExterior + ')' }">
        <div class="cave-overview-shade"></div>
        <div class="cave-overview-topbar">
          <div class="cave-title-section"><div class="cave-icon"><span class="cave-xian-glyph">府</span></div><div><h3>{{ caveData.caveName }}</h3><p>洞府等级 Lv.{{ caveData.caveLevel }} · {{ userData.realmName }} · 修为 {{ displayPlayerExp }} / {{ displayPlayerMaxExp }}</p></div></div>
          <Button type="warning" size="small" :loading="upgradingCave" @click="openCaveUpgrade">升级洞府</Button>
        </div>
        <div class="cave-overview-title"><span>洞天福地</span><small>点击建筑进入二级设施页面</small></div>
        <button type="button" class="cave-scene-hotspot cave-hotspot-settings" @click="showCaveStats"><strong>洞府设置</strong><small>调整十二时辰</small></button>
        <button v-for="fac in facilities" :key="'scene-' + fac.id" type="button" class="cave-scene-hotspot" :class="'scene-facility-' + fac.id" @click="enterFacility(fac.id)"><span class="cave-scene-hotspot-glyph" :style="{ background: fac.color }">{{ facilityGlyph(fac.id) }}</span><strong>{{ fac.name }}</strong><small>Lv.{{ fac.level }}</small></button>
        <button type="button" class="cave-scene-hotspot scene-facility-aura" @click="enterFacility('aura')"><span class="cave-scene-hotspot-glyph aura-hotspot-glyph">兽</span><strong>灵兽园</strong><small>真灵列表</small></button>
        <button type="button" class="cave-scene-hotspot scene-facility-market" @click="enterFacility('market')"><span class="cave-scene-hotspot-glyph market-hotspot-glyph">商</span><strong>珍宝阁</strong><small>商品兑换</small></button>
        <div class="cave-overview-resource"><span>灵气 <b>{{ Math.floor(caveData.spiritValue || 0) }}</b></span><span>累计收取 <b>{{ caveData.totalCollectedSpirit || 0 }}</b></span><span>可分配 <b>{{ 12 - usedSlots }}</b> 时辰</span></div>
      </section>

      <section v-else class="cave-farm-subpage" :style="{ backgroundImage: 'url(' + (settingsVisible ? caveExterior : (facilityBackdropMap[currentFacility] || caveExterior)) + ')' }">
        <div class="cave-subpage-shade"></div>
        <header class="cave-subpage-header"><div><span></span><h2>{{ settingsVisible ? '洞府设置' : currentFacility === 'market' ? '珍宝阁' : currentFacility === 'aura' ? '灵兽园' : getFacility(currentFacility).name }}</h2><p>{{ settingsVisible ? '调整时辰，提升对应产出' : currentFacility === 'market' ? '每日商店与珍宝兑换' : currentFacility === 'aura' ? '真灵列表与养成' : '' }}</p></div><Button type="warning" size="small" @click="returnToCave">返回洞府</Button></header>
        <div v-if="settingsVisible" class="cave-settings-page">
          <div class="cave-settings-heading"><div><span>洞府设置</span><strong>调整时辰，提升对应产出</strong></div><b>{{ usedSlots }} / 12 时辰</b></div>
          <div class="cave-resource-strip"><div class="cave-resource-item"><span class="resource-icon resource-icon-slots"><span class="cave-xian-glyph">时</span></span><span><small>时辰分配</small><strong>{{ usedSlots }} / 12</strong></span></div><div class="cave-resource-item"><span class="resource-icon resource-icon-spirit"><span class="cave-xian-glyph">气</span></span><span><small>当前灵气</small><strong>{{ Math.floor(caveData.spiritValue || 0) }}</strong></span></div><div class="cave-resource-item"><span class="resource-icon resource-icon-total"><span class="cave-xian-glyph">录</span></span><span><small>累计收取</small><strong>{{ caveData.totalCollectedSpirit || 0 }}</strong></span></div></div>
          <div class="cave-time-setting-list"><div v-for="fac in facilities" :key="'setting-' + fac.id" class="cave-time-setting-row"><span class="cave-time-setting-glyph" :style="{ background: fac.color }">{{ facilityGlyph(fac.id) }}</span><div><strong>{{ fac.name }}</strong><small>{{ fac.id === 'cultivation' ? '灵气产出' : '设施成功率' }} · {{ fac.points }} 时辰</small></div><button class="alloc-btn minus" :disabled="fac.points <= 0" @click="decreasePoints(fac.id)">−</button><b>{{ fac.points }}</b><button class="alloc-btn plus" :disabled="usedSlots >= 12" @click="increasePoints(fac.id)">+</button></div></div>
          <div class="cave-settings-footer"><span v-if="hasTimeSlotChanges">有未保存的时辰修改</span><span v-else>还可分配 {{ 12 - usedSlots }} 个时辰</span><Button :type="hasTimeSlotChanges ? 'warning' : 'primary'" :loading="timeSlotSaving" @click="saveTimeSlotAllocation">保存设置</Button></div>
        </div>
        <div v-else class="cave-subpage-content"><div v-show="currentFacility === 'cultivation'" class="farm-child-panel"><CaveFarmCultivation :level="getFacility('cultivation').level" :points="getFacilityPoints('cultivation')" :baseOutput="getFacility('cultivation').baseOutput" :perPointBonus="getFacility('cultivation').perPointBonus" :spiritValue="caveData.spiritValue" :playerLevel="playerLevel" :playerExp="playerExp" :playerMaxExp="playerMaxExp" :breachInfo="breachStuff" @upgrade="openFacilityUpgrade('cultivation')" @collected="onSpiritCollected" @breached="onBreakthrough" /></div><div v-show="currentFacility === 'alchemy'" class="farm-child-panel"><CaveFarmAlchemy :level="getFacility('alchemy').level" :points="getFacilityPoints('alchemy')" :baseSuccessRate="Number(getFacilitySuccessRateValue('alchemy') * 100)" @upgrade="openFacilityUpgrade('alchemy')" @refined="onAlchemyRefined" @received="onAlchemyReceived" /></div><div v-show="currentFacility === 'forging'" class="farm-child-panel"><CaveFarmForging :level="getFacility('forging').level" :points="getFacilityPoints('forging')" :successBonus="(getFacilityPoints('forging') * 5)" @upgrade="openFacilityUpgrade('forging')" @forged="onForgingDone" @smelted="onSmeltDone" @treasureForged="onTreasureDone" @elementRotated="onElementDone" @sublimated="onSublimationDone" @received="onForgingReceived" /></div><div v-show="currentFacility === 'market'" class="farm-child-panel"><CaveFarmMarket @purchased="$emit('user-activity-refresh')" @synthesized="$emit('user-activity-refresh')" /></div><div v-show="currentFacility === 'aura'" class="farm-child-panel"><CaveFarmAura :theme="caveTheme" /></div></div>
      </section>
    </div>

    <!-- 洞府升级弹窗 -->
      <Modal v-model="caveUpgradeVisible" title="洞府升级" width="500" :class-name="caveTheme === 'blue' ? 'cave-upgrade-modal cave-upgrade-modal-blue' : 'cave-upgrade-modal'">
        <div class="cave-upgrade-content" v-if="upgradeInfo">
          <div class="upgrade-info">
            <h4>Lv.{{ caveData.caveLevel }} → Lv.{{ upgradeInfo.nextLevel }}</h4>
            <div class="upgrade-desc-box">
              {{ upgradeInfo.description || `设施等级上限提升至 Lv.${upgradeInfo.facilityLevelLimit}` }}
            </div>
          </div>
          <div class="upgrade-requirements">
            <div class="req-title">升级消耗：</div>
            <div class="req-exp" v-if="upgradeInfo.needExp">
              所需修为：{{ upgradeInfo.currentExp }} / {{ upgradeInfo.needExp }}
              <Progress :percent="(upgradeInfo.currentExp / upgradeInfo.needExp * 100)" :stroke-width="10" status="active" />
            </div>
            <div class="cost-list">
              <div class="cost-item" v-for="(cost, idx) in upgradeInfo.materials" :key="idx">
                <span>{{ cost.name }}</span>
                <span :class="{ 'insufficient': cost.have < cost.need }">
                    {{ cost.have }}/{{ cost.need }}
                  </span>
              </div>
            </div>
          </div>
        </div>
        <div slot="footer">
          <Button @click="caveUpgradeVisible = false">取消</Button>
          <Button type="primary" :loading="upgradingCave" :disabled="!canUpgradeCave" @click="handleUpgradeCave">
            确认升级
          </Button>
        </div>
      </Modal>
  <!-- 设施升级弹窗 -->
  <Modal v-model="facilityUpgradeVisible" title="设施升级" width="400" :class-name="caveTheme === 'blue' ? 'cave-upgrade-modal cave-upgrade-modal-blue' : 'cave-upgrade-modal'">
    <div v-if="currentUpgradeFacility" class="facility-upgrade-content">
      <div class="facility-upgrade-header" :style="{ background: currentUpgradeFacility.color }">
        <span class="cave-upgrade-glyph">{{ facilityGlyph(currentUpgradeFacility.id) }}</span>
        <h3>{{ currentUpgradeFacility.name }}</h3>
        <div class="level-text">Lv.{{ currentUpgradeFacility.level }} → Lv.{{ currentUpgradeFacility.level + 1 }}</div>
        <!-- 达到上限提示 -->
        <div v-if="!currentUpgradeFacility.canUpgrade && currentUpgradeFacility.level >= currentUpgradeFacility.maxLevel" class="limit-tip">
          已达洞府等级上限
        </div>
      </div>

      <!-- 升级效果 -->
      <div class="upgrade-effects">
        <h4>升级效果：</h4>
        <div class="upgrade-desc-box facility">
          {{ (facilityUpgradeConfig && facilityUpgradeConfig.description) || `设施等级提升至 Lv.${currentUpgradeFacility.targetLevel}` }}
        </div>
      </div>


      <!-- 升级材料 -->
      <div class="upgrade-materials" v-if="facilityUpgradeMaterials && facilityUpgradeMaterials.length > 0">
        <h4>升级材料：</h4>
        <div class="material-list">
          <div
            v-for="(mat, idx) in facilityUpgradeMaterials"
            :key="idx"
            class="material-row"
            :class="{ 'insufficient': mat.have < mat.need }"
          >
            <div class="mat-info">
              <span class="mat-name">{{ mat.name }}</span>
            </div>
            <span class="mat-count" :class="{ 'enough': mat.have >= mat.need, 'not-enough': mat.have < mat.need }">
            {{ mat.have || 0 }} / {{ mat.need }}
          </span>
          </div>
        </div>
        <!-- 材料不足提示 -->
        <div v-if="!materialEnough" class="material-warning">
          <span class="cave-xian-glyph cave-status-warning">缺</span>
          材料不足，无法升级
        </div>
      </div>

      <!-- 无需材料 -->
      <div v-else-if="currentUpgradeFacility.canUpgrade" class="no-cost">
        <span class="cave-xian-glyph cave-status-success">成</span>
        该等级升级无需消耗材料，可直接升级
      </div>

      <!-- 已达上限 -->
      <div v-else-if="currentUpgradeFacility.level >= currentUpgradeFacility.maxLevel" class="no-cost limit">
        <span class="cave-xian-glyph cave-status-locked">锁</span>
        请先升级洞府以提升设施等级上限（当前上限 Lv.{{ currentUpgradeFacility.maxLevel }}）
      </div>
    </div>
    <div slot="footer">
      <Button @click="facilityUpgradeVisible = false">取消</Button>
      <Button
        type="primary"
        :loading="upgradingFacility === currentFacility"
        :disabled="!canUpgradeFacility"
        @click="handleFacilityUpgrade"
      >
        确认升级
      </Button>
    </div>
  </Modal>

  </div>
</template>

<script>
import CaveFarmCultivation from './cave-farm/CaveFarmCultivation.vue';
import CaveFarmAlchemy from './cave-farm/CaveFarmAlchemy.vue';
import CaveFarmForging from './cave-farm/CaveFarmForging.vue';
import CaveFarmMarket from './cave-farm/CaveFarmMarket.vue';
import CaveFarmAura from './cave-farm/CaveFarmAura.vue';


export default {
  name: 'CaveFarmSystem',
  // 添加props接收父组件数据
  props: {
    visible: Boolean,
    playerLevel: Number,
    playerExp: Number,
    playerMaxExp: Number
  },
  watch: {
    visible: {
      immediate: true,
      handler(newVal) {
        if (newVal) {
          this.initData()
        }
      }
    },
    currentFacility(value) {
      if (this.isMobile && value) {
        this.mobileDetailVisible = true
      }
    },
    breachStuff() {
      this.updateCustomCollectDefault()
    },
    'caveData.spiritValue'() {
      this.updateCustomCollectDefault()
    },
    playerExp() {
      this.updateCustomCollectDefault()
    },
    playerMaxExp() {
      this.updateCustomCollectDefault()
    },
  },
  data() {
    return {
      // 主题切换口：改为 'classic' 即恢复原米金洞府配色。
      caveTheme: 'blue',
      isMobile: false,
      mobileDetailVisible: false,
      // 状态管理
      hasCave: false,
      caveLoading: true,
      showCreatePanel: true,  // 控制创建面板显示
      isCreating: false,
      upgradingCave: false,
      isBreakingThrough: false,

      // 洞府基础数据
      caveData: {
        id: '',
        caveName: '',
        caveLevel: 1,
        currentExp: 0,
        spiritValue: 0,
        totalCollectedSpirit: 0,
        realmLevel: 1,
        realmName: '练气期',
        lastSpiritUpdate: new Date()
      },
      currentLevelConfig: {},

      // 创建洞府
      newCaveName: '青云洞府',
      createRequirements: [],
      caveExterior: require('@/assets/spirit-farm/dongfu-exterior-v1.jpg'),
      facilityBackdrop: require('@/assets/spirit-farm/buildings/danfang-interior.jpg'),
      facilityBackdropMap: {
        cultivation: require('@/assets/spirit-farm/buildings/danfang-interior.jpg'),
        alchemy: require('@/assets/spirit-farm/buildings/danfang-interior.jpg'),
        forging: require('@/assets/spirit-farm/buildings/forge-interior.jpg'),
        aura: require('@/assets/spirit-farm/dongfu-exterior-v1.jpg')
      },

      // 十二时辰分配
      timeSlots: [],
      timeSlotSaving: false,
      hasTimeSlotChanges: false,

      // 设施数据
      facilities: [
        {
          id: 'cultivation',
          name: '修炼室',
          icon: '修',
          color: '#19be6b',
          level: 1,
          points: 0,
          baseOutput: 100,
          perPointBonus: 0.1
        },
        {
          id: 'alchemy',
          name: '炼丹房',
          icon: '丹',
          color: '#ff6b6b',
          level: 1,
          points: 0,
          baseOutput: 10,
          perPointBonus: 0.05
        },
        {
          id: 'forging',
          name: '炼器室',
          icon: '器',
          color: '#2db7f5',
          level: 1,
          points: 0,
          baseOutput: 5,
          perPointBonus: 0.05
        }
      ],
      currentFacility: null,
      settingsVisible: false,

      // 升级相关
      caveUpgradeVisible: false,
      upgradeInfo: null,
      facilityUpgradeVisible: false,
      currentUpgradeFacility: null,
      materialEnough: false,
      facilityUpgradeConfig: null,
      facilityUpgradeMaterials: [],
      upgradingFacility: null,

      // 境界突破
      breachStuff: null,

      // 炼丹炼器
      forgeVisible: false,
      recipeList: [],
      availableRecipes: [],
      availableBlueprints: [],

      // 定时器
      spiritTimer: null,
      // 新增：洞府状态
      //createCaveVisible: false, // 创建弹窗
      materialList : [],
      // 创建洞府所需材料（可从后端获取，也可前端配置）
      /*caveCreateCost: [
        { name: '灵石', glyph: '石', need: 5000, have: 0, itemId: 'coin' },
        { name: '建府令', glyph: '令', need: 1, have: 0, itemId: 10001 },
        { name: '玄铁矿', glyph: '矿', need: 10, have: 0, itemId: 10002 }
      ],*/
      // 修炼室数据
      cultivationData: {
        spirit: 1000, // 当前灵气
        totalCollected: 0
      },
      customCollectAmount: 0,
      // 炼丹相关 - 复用你原有数据结构
      //alchemyModalVisible: false,
      selectedRecipe: null,
      isRefining: false,
      userData: null,
      refiningProgress: 0,
      recipeSearch: '',
      auxiliaryNums: [],

      // 锻造相关 - 复用你原有数据结构
      forgingModalVisible: false,
      forgeTab: 'blueprints',
      blueprintSearch: '',
      selectedBlueprint: null,
      tuzhiingList1: [], // 委托列表

    }
  },
  components: { CaveFarmCultivation, CaveFarmAlchemy, CaveFarmForging, CaveFarmMarket, CaveFarmAura },
  computed: {
    assignedSlots() {
      return this.timeSlots.filter(s => s.facilityType).length
    },
    displayPlayerExp() {
      return Math.floor(this.playerExp || 0)
    },
    displayPlayerMaxExp() {
      return this.playerMaxExp || 0
    },
    totalCollected() {
      return this.caveData.totalCollectedSpirit || 0
    },
    usedSlots() {
      return this.facilities.reduce((sum, f) => sum + f.points, 0)
    },
    canCreate() {
      return this.createRequirements.every(item => item.have >= item.need) &&
        this.newCaveName.length >= 2 &&
        this.newCaveName.length <= 6
    },
    canUpgradeCave() {
      if (!this.upgradeInfo || !this.upgradeInfo.materials) return false
      return this.upgradeInfo.materials.every(m => m.have >= m.need) &&
        this.upgradeInfo.currentExp >= this.upgradeInfo.needExp
    },
    canUpgradeFacility() {
      if (!this.currentUpgradeFacility) return false
      if (this.facilityUpgradeMaterials.length === 0) return true
      return this.facilityUpgradeMaterials.every(m => m.have >= m.need)
    },
    expDiffNeeded() {
      return Math.max(0, this.displayPlayerMaxExp - this.displayPlayerExp)
    },
  },
  mounted() {
    this.checkMobile()
    // 不在这里调用 initData，由 watch visible 控制
    // 页面可见性变化时重新计算
    document.addEventListener('visibilitychange', this.handleVisibilityChange)
    window.addEventListener('resize', this.checkMobile)
  },

  beforeDestroy() {
    if (this.spiritTimer) clearInterval(this.spiritTimer)
    document.removeEventListener('visibilitychange', this.handleVisibilityChange)
    window.removeEventListener('resize', this.checkMobile)
  },
  methods: {
    checkMobile() {
      const responsive = this.$store && this.$store.state && this.$store.state.responsive
        ? this.$store.state.responsive
        : null
      this.isMobile = responsive ? !!responsive.isMobile : window.innerWidth <= 768
      if (!this.isMobile) {
        this.mobileDetailVisible = false
      }
    },
    backToFacilityList() {
      this.mobileDetailVisible = false
    },
    // ========== 初始化 ==========
    async initData() {
      this.userData = JSON.parse(localStorage.getItem("userActivity")) || {};
      this.caveLoading = true
      this.hasTimeSlotChanges = false
      this.currentFacility = null
      this.settingsVisible = false
      this.mobileDetailVisible = false
      try {
        await this.checkCaveStatus()
      } finally {
        this.caveLoading = false
        this.updateCustomCollectDefault()
      }
    },
     // 关闭创建面板
    closeCreatePanel() {
      this.showCreatePanel = false
      this.$Message.info('可随时点击【开辟洞府】按钮重新打开')
    },
    
    // 重新打开创建面板
    openCreatePanel() {
      this.showCreatePanel = true
    },
    
    async checkCaveStatus() {
      try {
        const res = await this.$http.get(`/gameCave/info?charaId=${this.getCookie('charaId')}`)
        if (res.data.status === 200 && res.data.data) {
          this.hasCave = true
          this.parseCaveData(res.data.data)
          await this.loadBreachInfo()
        } else {
          this.hasCave = false
          await this.loadCreateRequirements()
        }
      } catch (err) {
        this.$Message.error('获取洞府信息失败')
        this.hasCave = false
      }
    },
    // ========== 数据解析 ==========
    parseCaveData(data) {
      // 主数据
      this.caveData = {
        ...this.caveData,
        ...data
      }

      // 解析设施数据
      if (data.facilities) {
        data.facilities.forEach(fac => {
          const target = this.facilities.find(f => f.id === fac.id)
          if (target) {
            target.level = fac.level
            target.points = fac.points || 0
            // 从后端配置读取实际数值，这里简化处理
            if (fac.level >= 1) {
              target.baseOutput =fac.baseOutput
              target.perPointBonus =fac.perPointBonus
            }
          }
        })
      }

      // 解析时辰分配
      if (data.timeSlots) {
        this.timeSlots = data.timeSlots.map((slot, index) => ({
          slotIndex: index,
          earthlyBranch: slot.branch || this.getEarthlyBranch(index),
          facilityType: slot.facility || null
        }))
      } else {
        // 初始化默认时辰
        this.initTimeSlots()
      }

      // 获取升级信息
      this.loadUpgradeInfo()
    },
    initTimeSlots() {
      const branches = ['子', '丑', '寅', '卯', '辰', '巳', '午', '未', '申', '酉', '戌', '亥']
      this.timeSlots = branches.map((branch, index) => ({
        slotIndex: index,
        earthlyBranch: branch,
        facilityType: null
      }))
    },
    getEarthlyBranch(index) {
      const branches = ['子', '丑', '寅', '卯', '辰', '巳', '午', '未', '申', '酉', '戌', '亥']
      return branches[index]
    },
    // ========== 创建洞府 ==========
    async loadCreateRequirements() {
      try {
        const res = await this.$http.get(`/gameCave/createRequirements?charaId=${this.getCookie('charaId')}`)
        if (res.data.status === 200) {
          this.createRequirements = res.data.data || []
        }
      } catch (err) {
        // 使用默认配置
        this.createRequirements = [
          { name: '灵石', itemId: 'coin', need: 5000, have: 0, icon: '' },
          { name: '建府令', itemId: '10001', need: 1, have: 0, icon: '' },
          { name: '玄铁矿', itemId: '10002', need: 10, have: 0, icon: '' }
        ]
      }
    },
    async handleCreateCave() {
      if (!this.canCreate) return
      this.isCreating = true
      try {
        const res = await this.$http.post(`/gameCave/create/?charaId=${this.getCookie('charaId')}&caveName=${this.newCaveName}`);

        if (res.data.status === 200) {
          this.$Message.success('洞府开辟成功！')
          this.parseCaveData(res.data.data)
          this.hasCave = true
        } else {
          this.$Message.error(res.data.msg || '创建失败')
        }
      } catch (err) {
        this.$Message.error(err.data.msg || '创建失败')
      } finally {
        this.isCreating = false
      }
    },
    // ========== 时辰分配 ==========
    updateFacilityPoints() {
      this.facilities.forEach(fac => {
        fac.points = this.timeSlots.filter(s => s.facilityType === fac.id).length
      })
    },

    increasePoints(facilityId) {
      if (this.usedSlots >= 12) return
      const fac = this.facilities.find(f => f.id === facilityId)
      if (fac) {
        fac.points++
        this.hasTimeSlotChanges = true
      }
    },
    decreasePoints(facilityId) {
      const fac = this.facilities.find(f => f.id === facilityId)
      if (fac && fac.points > 0) {
        fac.points--
        this.hasTimeSlotChanges = true
      }
    },
    async saveTimeSlotAllocation() {
      this.timeSlotSaving = true
      try {
        const slots = []
        let slotIndex = 0
        this.facilities.forEach(fac => {
          for (let i = 0; i < fac.points; i++) {
            slots.push({ index: slotIndex++, facility: fac.id })
          }
        })
        while (slots.length < 12) {
          slots.push({ index: slotIndex++, facility: null })
        }

        const res = await this.$http.post('/gameCave/assignTimeSlots', {
          charaId: this.getCookie('charaId'),
          slots: slots
        })

        if (res.data.status === 200) {
          this.$Message.success('时辰分配已保存')
          this.hasTimeSlotChanges = false
          this.updateFacilityPoints()
          this.checkCaveStatus()
        } else {
          this.$Message.error(res.data.msg)
        }
      } catch (err) {
        this.$Message.error((err.data && err.data.msg) || '保存失败')
      } finally {
        this.timeSlotSaving = false
      }
    },

    getFacilityPoints(facilityId) {
      return this.timeSlots.filter(s => s.facilityType === facilityId).length
    },

    getFacility(facilityId) {
      return this.facilities.find(f => f.id === facilityId) || {}
    },

    getPointBonus(facilityId) {
      const fac = this.getFacility(facilityId)
      return fac.perPointBonus || 0.1
    },
    getFacilitySuccessRateValue(facilityId) {
      const fac = this.getFacility(facilityId)
      const base = Number(fac.baseOutput || 0)
      const points = Number(fac.points || 0)
      const perPointBonus = Number(fac.perPointBonus || 0)
      return Math.max(0, Math.min(1, base + points * perPointBonus))
    },
    getFacilitySuccessRateText(facilityId) {
      return (this.getFacilitySuccessRateValue(facilityId) * 100).toFixed(0) + '%'
    },
    getAlchemyRecipePreviewRate(recipe) {
      const recipeBaseRate = Number(recipe && (recipe.baseRate != null ? recipe.baseRate : recipe.basicRate) || 0)
      const facility = this.getFacility('alchemy')
      const points = Number(facility.points || 0)
      const perPointBonus = Number(facility.perPointBonus || 0)
      const total = Math.max(0, Math.min(1, recipeBaseRate + points * perPointBonus))
      return (total * 100).toFixed(0) + '%'
    },
    // ========== 灵气收集 ==========
    /*calculateLocalSpiritGrowth() {
      // 前端简单的灵气增长计算（用于实时显示，实际以服务端为准）
      const cultivation = this.getFacility('cultivation')
      const points = cultivation.points
      if (points === 0) return

      const minutesPassed = 1 // 每分钟调用一次
      const hours = minutesPassed / 60
      const baseOutput = cultivation.baseOutput
      const perPointBonus = cultivation.perPointBonus

      const output = baseOutput * points * (1 + perPointBonus * points) * hours
      if (output > 0) {
        this.caveData.spiritValue += output
      }
    },*/

    async handleCollectSpirit(amount) {
      try {
        const res = await this.$http.post(`/gameCave/collectSpirit/?charaId=${this.getCookie('charaId')}&amount=${amount}`);
        if (res.data.status === 200) {
          this.$Message.success(res.data.data);
          this.$emit('exp-updated');
          this.$bus.$emit('caveCollectExp');
        } else {
          this.$Message.warning(res.data.msg);
        }
        await this.checkCaveStatus();
        await this.loadBreachInfo();
      } catch (err) {
        this.$Message.error(err.data.msg || '收取失败')
      }
    },

    async handleCustomCollect() {
      const maxCollectable = Math.floor(this.caveData.spiritValue || 0)
      const amount = Number(this.customCollectAmount)
      if (!Number.isInteger(amount) || amount <= 0) {
        this.$Message.warning('请输入大于0的整数收取数量')
        return
      }
      if (maxCollectable <= 0) {
        this.$Message.warning('当前暂无可收取灵气')
        return
      }
      if (amount > maxCollectable) {
        this.$Message.warning(`最多可收取 ${maxCollectable} 点灵气`)
        return
      }
      await this.handleCollectSpirit(amount)
    },

    updateCustomCollectDefault() {
      const spiritValue = Math.floor(this.caveData.spiritValue || 0)
      const expDiff = this.expDiffNeeded
      if (spiritValue === 0) {
        this.customCollectAmount = 0
      } else if (expDiff <= 0) {
        // 修为已满（准备突破或突破失败），允许收取全部灵气
        this.customCollectAmount = spiritValue
      } else if (spiritValue >= expDiff) {
        this.customCollectAmount = expDiff
      } else {
        this.customCollectAmount = spiritValue
      }
    },

    handleVisibilityChange() {
      if (!document.hidden) {
        // 页面重新可见时，重新获取数据以同步离线收益
        this.checkCaveStatus()
      }
    },

    // ========== 洞府升级 ==========
    async loadUpgradeInfo() {
      try {
        const res = await this.$http.get(`/gameCave/upgradeInfo?charaId=${this.getCookie('charaId')}`)
        if (res.data.status === 200) {
          this.upgradeInfo = res.data.data
        }
      } catch (err) {
        console.error('获取升级信息失败', err)
      }
    },


    // 新增：打开创建弹窗
    openCreateCave() {
      this.loadCreateRequirements() // 刷新材料数量
      this.createCaveVisible = true
    },
    // 新增：创建洞府
    async createCave() {
      // 检查材料是否足够
      const insufficient = this.caveCreateCost.filter(item => item.have < item.need)
      if (insufficient.length > 0) {
        this.$Message.warning(`材料不足：${insufficient.map(i => i.name).join('、')}`)
        return
      }

      this.isCreating = true
      try {
        const res = await this.$http.post('/gameCave/create', {
          charaId: this.getCookie('charaId'),
          name: '青云洞府' // 可让玩家输入，或使用默认
        })

        this.$Message.success('洞府创建成功！')
        this.createCaveVisible = false
        this.hasCave = true

        // 重新加载洞府数据
        this.caveData = res.data.data
        this.loadFacilityData()

      } catch (err) {
        this.$Message.error(err.data.msg || '创建失败')
      } finally {
        this.isCreating = false
      }
    },
    openCaveUpgrade() {
      this.loadUpgradeInfo()
      this.caveUpgradeVisible = true
    },

    async handleUpgradeCave() {
      this.upgradingCave = true
      try {
        // const res = await this.$http.post('/gameCave/upgrade', {
        //   charaId: this.getCookie('charaId')
        // })
        const res = await this.$http.post(`/gameCave/upgrade?charaId=${this.getCookie('charaId')}`)
        if (res.data.status === 200) {
          this.$Message.success('洞府升级成功！')
          this.parseCaveData(res.data.data)
          this.caveUpgradeVisible = false
        } else {
          this.$Message.error(res.data.msg)
        }
      } catch (err) {
        this.$Message.error(err.data.msg || '升级失败')
      } finally {
        this.upgradingCave = false
      }
    },

    // ========== 设施升级 ==========
    openFacilityUpgrade(facilityId) {
      const fac = this.getFacility(facilityId)
      this.currentUpgradeFacility = { ...fac, targetLevel: fac.level + 1 }
      this.loadFacilityUpgradeConfig(facilityId)
      this.facilityUpgradeVisible = true
    },
    async loadFacilityUpgradeConfig(facilityType, targetLevel) {
      try {
        this.facilityUpgradeConfig = null
        // 获取材料需求
        // 调用新的统一接口，不需要传 targetLevel，后台自动计算下一级
        const res = await this.$http.get('/gameCave/facilityUpgradeInfo', {
          params: {
            charaId: this.getCookie('charaId'),
            facilityType: facilityType  // cultivation/alchemy/forging
          }
        })
        if (res.data.status === 200) {
          const data = res.data.data
          this.currentUpgradeFacility = {
            id: data.facilityType,
            name: data.facilityName,
            level: data.currentLevel,
            targetLevel: data.nextLevel,
            maxLevel: data.maxLevel,
            canUpgrade: data.canUpgrade,
            color: this.getFacilityColor(facilityType),
            icon: this.getFacilityIcon(facilityType)
          }

          this.facilityUpgradeConfig = {
            outputBonus: data.outputBonus,
            pointBonus: data.successRateBonus,
            description: data.description || ''
          }

          this.facilityUpgradeMaterials = data.materials || [];
          this.canUpgradeFacility = data.canUpgrade;
          this.materialEnough =data.materialEnough;
        }
      } catch (err) {
        this.$Message.error('获取升级信息失败')
        this.facilityUpgradeMaterials = []
      }
    },

    async handleFacilityUpgrade() {
      if (!this.currentUpgradeFacility) return
      this.upgradingFacility = this.currentUpgradeFacility.id
      try {
        const res = await this.$http.post("/gameCave/upgradeFacility?charaId=" +
          this.getCookie("charaId") +
          "&facilityType=" + this.currentUpgradeFacility.id
        )
        if (res.data.status === 200) {
          this.$Message.success('设施升级成功！')
          const fac = this.facilities.find(f => f.id === this.currentUpgradeFacility.id)
          if (fac) fac.level++
          this.facilityUpgradeVisible = false
        } else {
          this.$Message.error(res.data.msg)
        }
      } catch (err) {
        this.$Message.error(err.data.msg || '升级失败')
      } finally {
        this.upgradingFacility = null
      }
    },
// ========== 境界突破 ==========
    async loadBreachInfo() {
      try {
        const res = await this.$http.post(`/gameRealm/getCaiRealm?charaId=${this.getCookie('charaId')}`)
        if (res.data.status === 200) {
          this.breachStuff = res.data.data
        }
      } catch (err) {
        console.error('获取境界信息失败', err)
      }
    },

    async handleBreakthrough() {
      this.isBreakingThrough = true
      try {
        const res = await this.$http.post(`/gameRealm/breakThroughTheRealm?charaId=${this.getCookie('charaId')}`)
        if (res.data.status === 200) {
          this.$Message.success(res.data.msg || '突破成功！')
        } else {
          this.$Message.warning(res.data.msg)
        }
      } catch (err) {
        this.$Message.warning(err.data.msg || '突破失败')
      } finally {
        this.$bus.$emit('caveBreakthrough');
        await this.checkCaveStatus()
        await this.loadBreachInfo()
        this.isBreakingThrough = false
        // 等父组件 props 更新后重新计算收取灵气默认值
        this.$nextTick(() => {
          this.updateCustomCollectDefault()
        })
      }
    },
    // 计算是否可创建（材料足够）  OLD 旧代码如下
    canCreateCave() {
      return this.caveCreateCost.every(item => item.have >= item.need)
    },

    // 原有方法保留...
    loadFacilityData() {
      // 加载设施数据、时辰分配等
      this.loadRecipeList()
      this.loadBlueprintList()
    },
    openforge() {
      this.forgeVisible = true;
    },
    loadRecipeList() {
      this.recipeList = this.$store.state.liandanList || []
    },

    loadBlueprintList() {
      // 复用你原有的加载图纸逻辑
      // this.$http.post(...)

    },
    getNodePosition(index) {
      const angle = (index * 30 - 90) * (Math.PI / 180)
      const radius = 120
      const center = 140
      const x = Math.cos(angle) * radius + center - 20
      const y = Math.sin(angle) * radius + center - 20
      return { left: `${x}px`, top: `${y}px` }
    },
    facilityGlyph(facilityId) {
      return ({ cultivation: '修', alchemy: '丹', forging: '器', aura: '兽' })[facilityId] || '府'
    },
    enterFacility(facilityId) {
      if (facilityId === 'aura' && this.isMobile) {
        this.currentFacility = null
        this.settingsVisible = false
        this.mobileDetailVisible = false
        this.$emit('open-aura-modal')
        return
      }
      this.currentFacility = facilityId
      this.settingsVisible = false
      if (facilityId === 'cultivation') {
        this.checkCaveStatus()
        this.loadBreachInfo()
      }
      if (this.isMobile) {
        this.mobileDetailVisible = true
      }
    },
    showCaveStats() {
      this.currentFacility = null
      this.settingsVisible = true
      if (this.isMobile) {
        this.mobileDetailVisible = true
      }
    },
    returnToCave() {
      this.currentFacility = null
      this.settingsVisible = false
      this.mobileDetailVisible = false
    },
    calculateFacilityOutput(facilityId) {
      const points = this.getFacilityPoints(facilityId)
      const facility = this.facilities.find(f => f.id === facilityId)
      const level = facility.level || 1
      if (facilityId === 'cultivation') {
        return { spirit: points * (50 + level * 10), exp: points * (100 + level * 20) }
      }
      return {}
    },
    // 境界突破确定
    changeRealm() {
      this.$http
        .post(
          "/gameRealm/breakThroughTheRealm?charaId=" + this.getCookie("charaId")
        )
        .then((res) => {
          this.$Message.success(res.data.msg);
        })
        .catch((err) => {
          this.$Message.warning(err.data.msg);
        });
    },
    collectSpirit() {
      if (this.cultivationData.spirit >= 100) {
        this.cultivationData.spirit -= 100
        this.cultivationData.totalCollected += 100
        this.$Message.success('收取灵气成功！')
      }
    },
    breakthrough() {
      this.$Message.success('突破成功！境界提升至筑基期！')
      this.currentRealm = '筑基期'
      this.nextRealm = '金丹期'
      this.cultivationData.spirit -= 1000
    },
    openAlchemyModal() {
      this.alchemyModalVisible = true
      this.selectedRecipe = null
    },
    openForgingModal() {
      this.forgingModalVisible = true
      this.selectedBlueprint = null
    },

    getGradeColor(grade) {
      const colors = { 'white': '#d9d9d9', 'green': '#19be6b', 'blue': '#2db7f5', 'purple': '#a855f7', 'orange': '#ff9900', 'red': '#ff4d4f' }
      return colors[grade] || '#d9d9d9'
    },
    getGradeTag(item) {
      return item.gradeName || '普通'
    },
    // 复用你原有的选择图纸逻辑，添加洞府加成
    selectRecipe(recipe) {
      this.selectedRecipe = recipe
      this.auxiliaryNums = []
      // 调用你原有的查询详情接口
      this.$http.post('/gameCharaEquip/queryForgingOrPills', {
        charaId: this.getCookie('charaId'),
        packItemIds: recipe.packItemId,
        formulaType: 2
      }).then(res => {
        this.selectedRecipe = { ...recipe, ...res.data.data }
        this.initAuxiliaryMaterials()
      })
    },

    initAuxiliaryMaterials() {
      // 复用你原有的初始化逻辑
      this.formulaMatZiList = []
      if (this.selectedRecipe.matList) {
        this.selectedRecipe.matList.forEach((mat, idx) => {
          if (mat.type === 1) {
            this.auxiliaryNums[idx] = mat.num
          } else {
            this.auxiliaryNums[idx] = Math.min(mat.haveNum, mat.num)
          }
        })
      }
    },

    onAuxiliaryChange(itemId, value, index) {
      // 复用你原有的变更逻辑
      this.$set(this.auxiliaryNums, index, value)
      // 更新 formulaMatZiList...
    },

    async startRefining() {
      // 复用你原有的炼丹逻辑，但添加洞府加成显示
      this.isRefining = true
      // ... 原有代码
      setTimeout(() => {
        this.isRefining = false
        this.$Message.success('炼丹完成！')
      }, 2000)
    },

    // 锻造相关方法 - 复用原有
    selectBlueprint(blueprint) {
      this.selectedBlueprint = blueprint
      // 调用原有查询详情...
    },
    getFacilityIcon(type) {
      return ({ cultivation: '修', alchemy: '丹', forging: '器', spirit: '气' })[type] || '府'
    },

    // ========== 设施颜色 ==========
    getFacilityColor(type) {
      const colorMap = {
        'cultivation': '#19be6b',       // 修炼室 - 绿色（生机）
        'alchemy': '#ff6b6b',           // 炼丹房 - 红色（火焰）
        'forging': '#2db7f5',           // 炼器室 - 蓝色（冷静/锻造）
        'spirit': '#ff9900',            // 灵气 - 橙色
        'default': '#999999'            // 默认灰色
      }
      return colorMap[type] || colorMap['default']
    },
    startForging() {
      // 复用你原有的锻造提交逻辑
      // 注意成功率要加上 getFacilityPoints('forging') * 0.05 的加成
    },

    // ========== 新组件事件处理 ==========
    onSpiritCollected() {
      this.checkCaveStatus()
      this.loadBreachInfo()
      this.$emit('exp-updated')
      this.$bus.$emit('caveCollectExp')
    },
    onBreakthrough() {
      this.checkCaveStatus()
      this.loadBreachInfo()
      this.$bus.$emit('caveBreakthrough')
    },
    onAlchemyRefined() {
      this.$emit('user-activity-refresh')
    },
    onAlchemyReceived() {
      this.$emit('user-activity-refresh')
    },
    onForgingDone() {
      this.$emit('user-activity-refresh')
    },
    onSmeltDone() {
      this.$emit('user-activity-refresh')
    },
    onTreasureDone() {
      this.$emit('user-activity-refresh')
    },
    onElementDone() {
      this.$emit('user-activity-refresh')
    },
    onSublimationDone() {
      this.$emit('user-activity-refresh')
    },
    onForgingReceived() {
      this.$emit('user-activity-refresh')
    },
  }
}
</script>

<style  scoped>
.cave-system-container {
  height: 100%;
  position: relative;
}

.cave-xian-glyph {
  display: inline-grid;
  place-items: center;
  min-width: 1.2em;
  color: currentColor;
  font-family: "KaiTi", "STKaiti", "FZKai-Z03", serif;
  font-weight: 900;
  line-height: 1;
}

.create-cave-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.create-cave-overlay .create-cave-panel {
  background: #fff;
  padding: 40px;
  border-radius: 12px;
  text-align: center;
  max-width: 500px;
  width: 90%;
}

.create-cave-overlay .create-cave-panel h2 {
  margin: 20px 0 10px;
  color: #333;
}

.create-cave-overlay .create-cave-panel .desc {
  color: #666;
  margin-bottom: 30px;
}

.create-cave-overlay .create-cave-panel .material-checklist {
  margin: 20px 0;
  text-align: left;
}

.create-cave-overlay .create-cave-panel .material-checklist .material-item {
  display: flex;
  align-items: center;
  padding: 12px;
  background: #f8f9fa;
  border-radius: 8px;
  margin-bottom: 10px;
  border: 1px solid #e8e8e8;
}

.create-cave-overlay .create-cave-panel .material-checklist .material-item.sufficient {
  border-color: #19be6b;
  background: #f6ffed;
}

.create-cave-overlay .create-cave-panel .material-checklist .material-item .mat-icon,
.create-cave-overlay .create-cave-panel .material-checklist .material-item .mat-icon-placeholder {
  width: 40px;
  height: 40px;
  border-radius: 6px;
  margin-right: 12px;
  background: #ddd;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  color: #fff;
}

.create-cave-overlay .create-cave-panel .material-checklist .material-item .mat-info {
  flex: 1;
}

.create-cave-overlay .create-cave-panel .material-checklist .material-item .mat-info .mat-name {
  font-weight: bold;
  color: #333;
}

.create-cave-overlay .create-cave-panel .material-checklist .material-item .mat-info .mat-count {
  font-size: 12px;
  color: #ed4014;
  margin-top: 4px;
}

.create-cave-overlay .create-cave-panel .material-checklist .material-item .mat-info .mat-count.enough {
  color: #19be6b;
}

.create-cave-overlay .create-cave-panel .cave-name-input {
  margin: 20px 0;
}

.create-cave-overlay .create-cave-panel .warning-text {
  color: #ed4014;
  font-size: 12px;
  margin-top: 10px;
}

.loading-container {
  height: 600px;
  position: relative;
}

.loading-container .spin-icon-load {
  animation: ani-spin 1s linear infinite;
}

@keyframes ani-spin {
  from { transform: rotate(0deg); }
  50% { transform: rotate(180deg); }
  to { transform: rotate(360deg); }
}

.cave-system-modal /deep/ .ivu-modal-body {
  padding: 0;
  max-height: 80vh;
}

.cave-system-layout {
  display: flex;
  height: 650px;
}

.cave-visual-panel {
  width: 350px;
  background: linear-gradient(180deg, #f8f9fa 0%, #e9ecef 100%);
  border-right: 1px solid #e8e8e8;
  display: flex;
  flex-direction: column;
}

.cave-visual-panel .quick-actions {
  padding: 15px;
  border-top: 1px solid #e8e8e8;
  background: #fff;
}

.cave-header-bar {
  padding: 12px 15px;
  background: #fff;
  border-bottom: 1px solid #e8e8e8;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.cave-header-bar .cave-title-section {
  display: flex;
  align-items: center;
  gap: 10px;
}

.cave-header-bar .cave-title-section .cave-icon {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
}

.cave-header-bar .cave-title-section h3 {
  margin: 0;
  font-size: 16px;
}

.cave-header-bar .cave-title-section .cave-meta {
  margin-top: 4px;
}

.cave-header-bar .cave-title-section .exp-text {
  font-size: 12px;
  color: #999;
  margin-left: 8px;
}

.cave-header-bar .cave-title-section .realm-text {
  font-size: 12px;
  color: #2d8cf0;
  font-weight: bold;
}

.time-wheel-container {
  height: 280px;
  position: relative;
  margin: 13px auto;
  width: 280px;
}

.time-wheel-container .wheel-center {
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  cursor: pointer;
  z-index: 10;
  box-shadow: 0 4px 15px rgba(102, 126, 234, 0.4);
}

.time-wheel-container .wheel-center .center-content {
  text-align: center;
}

.time-wheel-container .wheel-center .center-content .spirit-value {
  font-size: 20px;
  font-weight: bold;
}

.time-wheel-container .wheel-center .center-content .center-label {
  font-size: 11px;
  opacity: 0.9;
}

.time-wheel-container .wheel-center .center-content .center-sub {
  font-size: 10px;
  opacity: 0.8;
  margin-top: 2px;
}

.time-wheel-container .time-node {
  position: absolute;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: #fff;
  border: 2px solid #ddd;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s;
  z-index: 5;
}

.time-wheel-container .time-node:hover {
  transform: scale(1.2);
  z-index: 6;
}

.time-wheel-container .time-node.active {
  box-shadow: 0 0 10px currentColor;
}

.time-wheel-container .time-node.cultivation {
  background: rgba(25, 190, 107, 0.1);
  border-color: #19be6b;
  color: #19be6b;
}

.time-wheel-container .time-node.alchemy {
  background: rgba(255, 107, 107, 0.1);
  border-color: #ff6b6b;
  color: #ff6b6b;
}

.time-wheel-container .time-node.forging {
  background: rgba(45, 183, 245, 0.1);
  border-color: #2db7f5;
  color: #2db7f5;
}

.time-wheel-container .time-node .node-content {
  text-align: center;
}

.time-wheel-container .time-node .node-content .earthly-branch {
  font-size: 14px;
  font-weight: bold;
}

.time-wheel-container .time-node .node-content .facility-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: currentColor;
  margin: 2px auto 0;
}

.facility-overview {
  flex: 1;
  padding: 10px;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}

.overview-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
  padding-bottom: 8px;
  border-bottom: 1px solid #f0f0f0;
}

.overview-title {
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  color: #1f2d3d;
}

.slot-summary {
  display: flex;
  align-items: baseline;
  gap: 2px;
  font-size: 14px;
}

.slot-summary .used-slots {
  font-size: 20px;
  font-weight: 700;
  color: #2d8cf0;
}

.slot-summary .separator {
  color: #808695;
  margin: 0 2px;
}

.slot-summary .total-slots {
  font-size: 16px;
  font-weight: 600;
  color: #515a6e;
}

.slot-summary .label {
  font-size: 12px;
  color: #808695;
  margin-left: 4px;
}

.facility-card-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.facility-card-mini {
  background: #f8f9fa;
  border-radius: 10px;
  padding: 10px 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  border: 1px solid #e8e8e8;
  transition: all 0.2s ease;
  cursor: pointer;
}

.facility-card-mini:hover {
  border-color: #2d8cf0;
  background: #f0f7ff;
}

.facility-card-mini.active {
  border-color: #2d8cf0;
  background: linear-gradient(135deg, rgba(45, 140, 240, 0.05), rgba(255, 255, 255, 0.95));
}

.fac-card-top {
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
}

.fac-card-top .fac-icon {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.fac-card-top .fac-name {
  font-weight: 600;
  font-size: 15px;
  color: #1f2d3d;
  flex: 1;
}

.fac-card-top .fac-level-tag {
  font-size: 12px;
  color: #808695;
  background: #f0f0f0;
  padding: 2px 8px;
  border-radius: 10px;
}

.fac-alloc-controls {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
}

.alloc-btn {
  width: 34px;
  height: 34px;
  border-radius: 50%;
  border: 2px solid #b58a52;
  background: #f5ead1;
  color: #856133;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  padding: 0;
  font-size: 20px;
  font-weight: 700;
  line-height: 1;
}

.alloc-btn:hover:not(:disabled) {
  background: #b58a52;
  border-color: #956b38;
  color: #fff8df;
  transform: scale(1.1);
}

.alloc-btn:disabled {
  border-color: #d8cdb7 !important;
  background: #eee8dc !important;
  color: #b7aa93 !important;
  cursor: not-allowed;
}

.alloc-btn.minus {
  color: #a66c4d;
  border-color: #c98d6d;
  background: #f9e9dc;
}

.alloc-btn.minus:hover:not(:disabled) {
  background: #a66c4d;
  border-color: #875338;
  color: #fff7e3;
}

.alloc-display {
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 50px;
}

.alloc-value {
  font-size: 22px;
  font-weight: 700;
  color: #6b8f70;
  line-height: 1;
}

.alloc-label {
  font-size: 11px;
  color: #9a8462;
  margin-top: 2px;
}

.fac-effect-text {
  text-align: center;
  font-size: 12px;
  color: #927956;
}

.fac-effect-text .efficiency {
  color: #19be6b;
  font-weight: 600;
  margin-left: 4px;
}

.overview-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px solid #f0f0f0;
}

.remaining-hint,
.full-hint,
.unsaved-hint {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: #808695;
}

.full-hint {
  color: #19be6b;
}

.unsaved-hint {
  color: #ff9900;
  font-weight: 600;
}

.cave-detail-area {
  flex: 1;
  background: #fff;
  overflow: hidden;
}

.welcome-panel {
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
}

.welcome-panel .welcome-content {
  padding: 40px;
}

.welcome-panel .welcome-content h2 {
  margin: 20px 0 10px;
  color: #333;
}

.welcome-panel .welcome-content p {
  color: #999;
  margin-bottom: 30px;
}

.welcome-panel .welcome-content .stats-overview {
  display: flex;
  gap: 40px;
  justify-content: center;
}

.welcome-panel .welcome-content .stats-overview .stat-box {
  padding: 20px 30px;
  background: #f8f9fa;
  border-radius: 12px;
}

.welcome-panel .welcome-content .stats-overview .stat-box .stat-num {
  font-size: 32px;
  font-weight: bold;
  color: #2db7f5;
}

.welcome-panel .welcome-content .stats-overview .stat-box .stat-label {
  color: #666;
  margin-top: 5px;
}

.welcome-panel .welcome-content .cave-tips {
  color: #999;
  font-size: 12px;
  line-height: 1.8;
}

.facility-panel {
  height: 100%;
  overflow: hidden;
}

.facility-panel .panel-header {
  padding: 20px;
  color: #fff;
  display: flex;
  align-items: center;
  gap: 15px;
}

.facility-panel .panel-header .header-title {
  flex: 1;
}

.facility-panel .panel-header .header-title h3 {
  margin: 0;
  font-size: 20px;
}

.facility-panel .panel-header .header-title p {
  margin: 5px 0 0;
  opacity: 0.9;
  font-size: 13px;
}

.facility-panel .facility-actions {
  padding: 20px;
  border-bottom: 1px solid #f0f0f0;
  display: flex;
  gap: 10px;
}

.facility-panel .facility-preview {
  padding: 20px;
}

.facility-panel .facility-preview h4 {
  margin-bottom: 15px;
  color: #333;
}

.facility-panel .facility-preview .recipe-preview-list {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 10px;
}

.facility-panel .facility-preview .recipe-preview-list .preview-item {
  padding: 15px;
  background: #f8f9fa;
  border-radius: 8px;
  display: flex;
  align-items: center;
  gap: 10px;
  border: 1px solid #e8e8e8;
}

.facility-panel .facility-preview .recipe-preview-list .preview-item.can-make {
  border-color: #19be6b;
  background: #f6ffed;
}

.facility-panel .facility-preview .recipe-preview-list .preview-item .item-icon,
.facility-panel .facility-preview .recipe-preview-list .preview-item .item-icon-placeholder {
  width: 40px;
  height: 40px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-weight: bold;
}

.facility-panel .facility-preview .recipe-preview-list .preview-item .item-icon-placeholder {
  background: #ccc;
}

.facility-panel .facility-preview .recipe-preview-list .preview-item .item-name {
  flex: 1;
  font-size: 14px;
}

.facility-panel .facility-preview .recipe-preview-list .preview-item .item-rate {
  font-size: 12px;
  color: #ff9900;
  font-weight: bold;
}

.cultivation-panel .spirit-card,
.cultivation-panel .exp-card {
  margin: 10px;
}

.current-level-exp {
  margin-bottom: 12px;
  font-size: 14px;
  font-weight: 600;
  color: #ff8c00;
}

.cultivation-panel .spirit-card .ivu-card-head,
.cultivation-panel .exp-card .ivu-card-head {
  background: #f8f9fa;
}

.cultivation-panel .spirit-display {
  text-align: center;
}

.cultivation-panel .spirit-display .spirit-circle {
  width: 120px;
  height: 120px;
  border-radius: 50%;
  border: 3px solid #ff9900;
  margin: 0 auto 15px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: radial-gradient(circle, rgba(255,153,0,0.1) 0%, transparent 70%);
}

.cultivation-panel .spirit-display .spirit-circle .spirit-amount {
  font-size: 32px;
  font-weight: bold;
  color: #ff9900;
}

.cultivation-panel .spirit-display .spirit-circle .spirit-unit {
  font-size: 12px;
  color: #999;
}

.cultivation-panel .spirit-display .spirit-rate {
  color: #666;
  margin-bottom: 15px;
}

.cultivation-panel .hint-text {
  text-align: center;
  color: #999;
  font-size: 12px;
  margin-top: 10px;
}

.cultivation-panel .breakthrough-card {
  margin: 10px;
}

.cultivation-panel .breakthrough-card .breakthrough-content .current-realm {
  font-size: 18px;
  font-weight: bold;
  color: #2db7f5;
  margin-bottom: 15px;
  text-align: center;
}

.cultivation-panel .breakthrough-card .breakthrough-content .breakthrough-require {
  margin-bottom: 20px;
}

.cultivation-panel .breakthrough-card .breakthrough-content .breakthrough-require ul {
  list-style: none;
  margin-top: 10px;
}

.cultivation-panel .breakthrough-card .breakthrough-content .breakthrough-require ul li {
  padding: 5px 0;
  color: #666;
}

.cultivation-panel .breakthrough-card .breakthrough-content .breakthrough-require ul li.met {
  margin-top: 4px;
  color: #19be6b;
  text-decoration: line-through;
}

.inner-modal /deep/ .ivu-modal-body {
  padding: 0;
}

.alchemy-layout-inner,
.forge-layout-inner {
  display: flex;
  height: 600px;
}

.alchemy-layout-inner .alchemy-furnace-area,
.forge-layout-inner .forge-left {
  flex: 1;
  padding: 20px;
  overflow-y: auto;
}

.alchemy-layout-inner .alchemy-recipe-list,
.forge-layout-inner .forge-right {
  width: 300px;
  border-left: 1px solid #e8e8e8;
  background: #f8f9fa;
  padding: 20px;
  overflow-y: auto;
}

.cave-upgrade-content .upgrade-info {
  background: #f8f9fa;
  padding: 15px;
  border-radius: 8px;
  margin-bottom: 20px;
}

.cave-upgrade-content .upgrade-info h4 {
  margin-bottom: 10px;
  color: #333;
}

.cave-upgrade-content .upgrade-info ul {
  list-style: none;
  padding-left: 0;
}

.cave-upgrade-content .upgrade-info ul li {
  padding: 5px 0;
  color: #666;
}

.cave-upgrade-content .upgrade-info ul li:before {
  content: "✓ ";
  color: #19be6b;
  font-weight: bold;
}

.upgrade-desc-box {
  background: #f8f9fa;
  border-left: 4px solid #2d8cf0;
  color: #515a6e;
  line-height: 1.8;
  padding: 12px 14px;
  border-radius: 6px;
}

.upgrade-desc-box.facility {
  border-left-color: #19be6b;
}

.cave-upgrade-content .upgrade-requirements .req-title {
  font-weight: bold;
  margin-bottom: 10px;
}

.cave-upgrade-content .upgrade-requirements .req-exp {
  margin-bottom: 15px;
}

.cave-upgrade-content .upgrade-requirements .cost-list .cost-item {
  display: flex;
  justify-content: space-between;
  padding: 8px 0;
  border-bottom: 1px solid #f0f0f0;
}

.cave-upgrade-content .upgrade-requirements .cost-list .cost-item .insufficient {
  color: #ed4014;
}

.facility-upgrade-content .facility-upgrade-header {
  padding: 30px;
  text-align: center;
  color: #fff;
  margin: -16px -16px 20px -16px;
  border-radius: 4px 4px 0 0;
}

.facility-upgrade-content .facility-upgrade-header h3 {
  margin: 10px 0 5px;
}

.facility-upgrade-content .facility-upgrade-header .level-text {
  opacity: 0.9;
}

.facility-upgrade-content .upgrade-effects {
  margin-bottom: 20px;
}

.facility-upgrade-content .upgrade-effects h4 {
  margin-bottom: 10px;
  color: #333;
}

.facility-upgrade-content .upgrade-effects ul {
  list-style: none;
  background: #f8f9fa;
  padding: 15px;
  border-radius: 6px;
}

.facility-upgrade-content .upgrade-effects ul li {
  padding: 5px 0;
  color: #19be6b;
}

.facility-upgrade-content .upgrade-effects ul li:before {
  content: "↑ ";
}

.facility-upgrade-content .upgrade-materials h4 {
  margin-bottom: 10px;
  color: #333;
}

.facility-upgrade-content .upgrade-materials .material-list .material-row {
  display: flex;
  justify-content: space-between;
  padding: 10px;
  background: #f8f9fa;
  margin-bottom: 8px;
  border-radius: 4px;
}

.facility-upgrade-content .upgrade-materials .material-list .material-row.insufficient {
  background: #fff1f0;
  color: #ed4014;
}

.facility-upgrade-content .no-cost {
  text-align: center;
  padding: 20px;
  color: #999;
}

.furnace-visual {
  text-align: center;
  padding: 40px;
}

.furnace-visual .furnace-img {
  max-width: 200px;
}

.materials-section {
  margin: 20px 0;
}

.materials-section .section-title {
  display: flex;
  justify-content: space-between;
  margin-bottom: 10px;
  font-weight: bold;
}

.materials-section .materials-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
  gap: 10px;
}

.materials-section .materials-grid .material-slot {
  padding: 10px;
  background: #f8f9fa;
  border-radius: 6px;
  text-align: center;
  border: 1px solid #e8e8e8;
}

.materials-section .materials-grid .material-slot.sufficient {
  border-color: #19be6b;
  background: #f6ffed;
}

.materials-section .materials-grid .material-slot .mat-name {
  font-size: 12px;
  margin-bottom: 5px;
}

.materials-section .materials-grid .material-slot .mat-count {
  font-size: 11px;
  color: #666;
  margin-bottom: 5px;
}

.alchemy-action {
  text-align: center;
  padding: 20px;
}

.alchemy-action .success-rate-display {
  font-size: 18px;
  margin-bottom: 15px;
}

.alchemy-action .success-rate-display .bonus-text {
  color: #ff9900;
  font-size: 14px;
  margin-left: 10px;
}

.recipe-item,
.blueprint-item {
  padding: 12px;
  background: #fff;
  border-radius: 6px;
  margin-bottom: 8px;
  cursor: pointer;
  border: 1px solid transparent;
}

.recipe-item:hover,
.blueprint-item:hover,
.recipe-item.active,
.blueprint-item.active {
  border-color: #2db7f5;
  background: #f0f5ff;
}

.recipe-item .recipe-name,
.blueprint-item .blueprint-name {
  font-weight: 500;
  margin-bottom: 4px;
}

.recipe-item .recipe-meta,
.blueprint-item .blueprint-meta {
  font-size: 12px;
  color: #999;
}

.cave-mobile-back {
  padding: 12px 16px 0;
}

.wild-xinfa-cultivation-card {
  margin-top: 16px;
  border-radius: 12px;
  overflow: hidden;
}

.wild-xinfa-cultivation-title {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.wild-xinfa-empty {
  padding: 14px;
  text-align: center;
  color: #999;
  background: #f8fafc;
  border-radius: 8px;
}

.wild-xinfa-cultivation-list {
  display: grid;
  gap: 12px;
}

.wild-xinfa-cultivation-item {
  padding: 12px;
  border: 1px solid #e8eaec;
  border-radius: 10px;
  background: linear-gradient(135deg, #fffdf5, #f8fbff);
}

.wild-xinfa-cultivation-item.active {
  border-color: #19be6b;
  box-shadow: 0 0 0 2px rgba(25, 190, 107, 0.1);
}

.wild-xinfa-cultivation-head {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 10px;
}

.wild-xinfa-name {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  font-weight: 600;
}

.wild-xinfa-meta {
  margin-top: 4px;
  color: #808695;
  font-size: 12px;
}

.wild-xinfa-equip-btn {
  margin: 0 0 10px;
}

.wild-xinfa-layer-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(210px, 1fr));
  gap: 10px;
}

.wild-xinfa-layer-card {
  padding: 10px;
  border: 1px dashed #dcdee2;
  border-radius: 9px;
  background: rgba(255, 255, 255, 0.72);
}

.wild-xinfa-layer-card.unlocked {
  border-style: solid;
  border-color: #19be6b;
}

.wild-xinfa-layer-card.next {
  border-color: #ff9900;
  background: #fffaf0;
}

.layer-card-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
  font-weight: 600;
}

.layer-unlock-cost {
  margin: 6px 0 8px;
  font-size: 12px;
  color: #8c6d1f;
  line-height: 1.5;
}

.layer-affix-list {
  display: grid;
  gap: 5px;
  margin-bottom: 8px;
}

.layer-affix {
  font-size: 12px;
  color: #515a6e;
}

.layer-affix.locked {
  color: #999;
}

@media (max-width: 768px) {
  .cave-system-layout {
    display: block;
    height: auto;
    min-height: 0;
  }

  .cave-visual-panel,
  .cave-detail-area {
    width: 100%;
    min-width: 0;
  }

  .cave-visual-panel {
    border-right: none;
  }

  .facility-panel .facility-actions {
    flex-wrap: wrap;
  }

  .facility-panel .facility-actions .ivu-btn {
    flex: 1 1 calc(50% - 5px);
  }

  .facility-panel .facility-preview .recipe-preview-list {
    grid-template-columns: 1fr;
  }

  .cultivation-content .ivu-row {
    display: block;
  }

  .cultivation-content .ivu-col {
    width: 100%;
    max-width: 100%;
    display: block;
  }

  .wild-xinfa-layer-grid {
    grid-template-columns: 1fr;
  }
}

/* 灵田式洞府外景：场景负责入口，详情区继续承载原有设施组件。 */
.cave-system-layout--farm {
  display: grid;
  grid-template-columns: minmax(0, 1.08fr) minmax(430px, .92fr);
  height: 650px;
  background: #e9e4d5;
}

.cave-farm-visual-panel {
  width: auto;
  min-width: 0;
  overflow: auto;
  padding: 0 14px 14px;
  background: #f4f0e2;
  border-right: 1px solid #c9b989;
}

.cave-farm-header-bar {
  position: sticky;
  top: 0;
  z-index: 5;
  margin: 0 -14px;
  padding: 12px 14px;
  background: rgba(255, 252, 241, .96);
  border-bottom-color: #d7c79e;
}

.cave-farm-header-bar .cave-title-section .cave-icon {
  background: #6d8f70;
  border-radius: 5px;
  box-shadow: 0 4px 10px rgba(63, 83, 61, .18);
}

.cave-farm-header-bar .cave-title-section h3 {
  color: #4d3d28;
  font-family: "KaiTi", "STKaiti", serif;
}

.cave-farm-header-bar .realm-text {
  color: #5e8365;
}

.cave-farm-header-bar .exp-text {
  color: #8e7953;
}

.cave-farm-scene {
  position: relative;
  min-height: 330px;
  margin: 14px 0 10px;
  overflow: hidden;
  background-position: center;
  background-size: cover;
  border: 1px solid #a88b58;
  border-radius: 5px;
  box-shadow: inset 0 0 0 1px rgba(255, 247, 212, .45), 0 10px 24px rgba(73, 64, 42, .16);
}

.cave-farm-scene-shade {
  position: absolute;
  inset: 0;
  background: rgba(24, 36, 29, .13);
  pointer-events: none;
}

.cave-farm-scene-title {
  position: absolute;
  z-index: 2;
  left: 12px;
  top: 12px;
  display: grid;
  gap: 3px;
  padding: 7px 10px;
  color: #fff4cc;
  background: rgba(35, 45, 35, .66);
  border-left: 3px solid #e1bf63;
  text-shadow: 0 1px 2px #1c261d;
}

.cave-farm-scene-title span {
  font: 900 18px/1 "KaiTi", "STKaiti", serif;
}

.cave-farm-scene-title small {
  color: #e7d9a8;
  font-size: 10px;
}

.cave-farm-hotspot {
  position: absolute;
  z-index: 3;
  display: grid;
  justify-items: center;
  gap: 2px;
  min-width: 88px;
  padding: 5px 7px;
  color: #fff7d7;
  background: rgba(39, 53, 42, .72);
  border: 1px solid rgba(255, 229, 154, .82);
  border-radius: 4px;
  text-shadow: 0 1px 2px #17251d;
  cursor: pointer;
  transition: transform .18s ease, background .18s ease, box-shadow .18s ease;
}

.cave-farm-hotspot:hover,
.cave-farm-hotspot.active {
  background: rgba(111, 79, 32, .88);
  border-color: #ffe39e;
  box-shadow: 0 6px 15px rgba(45, 38, 22, .25);
  transform: translateY(-2px);
}

.cave-farm-hotspot-icon {
  display: grid;
  place-items: center;
  width: 30px;
  height: 30px;
  border: 1px solid #ffe8a8;
  border-radius: 50%;
  box-shadow: 0 2px 5px rgba(18, 28, 22, .2);
}

.cave-farm-hotspot strong {
  font-size: 11px;
  white-space: nowrap;
}

.cave-farm-hotspot small {
  color: #eadcac;
  font-size: 9px;
  white-space: nowrap;
}

.cave-hotspot-cultivation { left: 23%; top: 62%; }
.cave-hotspot-alchemy { left: 72%; top: 28%; }
.cave-hotspot-forging { left: 76%; top: 69%; }

.cave-farm-scene-core {
  position: absolute;
  z-index: 2;
  left: 50%;
  top: 49%;
  display: grid;
  justify-items: center;
  gap: 2px;
  min-width: 128px;
  padding: 9px 12px;
  color: #fff2bd;
  background: rgba(39, 52, 41, .62);
  border: 1px solid rgba(255, 228, 154, .6);
  border-radius: 5px;
  text-align: center;
  text-shadow: 0 1px 2px #16231b;
  transform: translate(-50%, -50%);
}

.cave-farm-scene-core strong {
  max-width: 130px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font: 700 13px/1.2 "KaiTi", "STKaiti", serif;
}

.cave-farm-scene-core small {
  color: #e5d59f;
  font-size: 9px;
}

.cave-resource-strip {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 7px;
  margin-bottom: 10px;
}

.cave-resource-item {
  display: flex;
  align-items: center;
  min-width: 0;
  gap: 7px;
  padding: 8px;
  color: #55452e;
  background: #fffaf0;
  border: 1px solid #d8c99e;
  border-radius: 4px;
}

.cave-resource-item > span:last-child {
  display: grid;
  min-width: 0;
  gap: 2px;
}

.cave-resource-item small {
  color: #8c7957;
  font-size: 9px;
}

.cave-resource-item strong {
  color: #5b704e;
  font-size: 13px;
  white-space: nowrap;
}

.resource-icon {
  display: grid;
  place-items: center;
  flex: 0 0 auto;
  width: 27px;
  height: 27px;
  color: #fff8dc;
  border-radius: 50%;
}

.resource-icon-slots { background: #92734b; }
.resource-icon-spirit { background: #5e9870; }
.resource-icon-total { background: #66839a; }

.cave-farm-facility-overview {
  margin: 0;
  padding: 10px;
  background: #fffaf0;
  border: 1px solid #d8c99e;
  border-radius: 4px;
  box-shadow: 0 4px 12px rgba(83, 68, 38, .08);
}

.cave-farm-facility-overview .overview-header {
  border-bottom-color: #e3d8bb;
}

.cave-overview-subtitle {
  display: block;
  margin-top: 3px;
  color: #998460;
  font-size: 10px;
}

.cave-farm-facility-overview .facility-card-list {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 7px;
}

.cave-farm-facility-card {
  min-width: 0;
  padding: 8px;
  gap: 6px;
  background: #f8f2e4;
  border-color: #ded0ac;
  border-radius: 4px;
}

.cave-farm-facility-card:hover,
.cave-farm-facility-card.active {
  background: #f3ead3;
  border-color: #bd9850;
}

.cave-farm-facility-card .fac-card-top {
  gap: 6px;
}

.cave-farm-facility-card .fac-icon {
  width: 28px;
  height: 28px;
  border-radius: 50%;
}

.cave-farm-facility-card .fac-name {
  overflow: hidden;
  color: #57452c;
  font-size: 12px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.cave-farm-facility-card .fac-level-tag {
  padding: 2px 5px;
  color: #8a7049;
  background: #eee4cb;
  border-radius: 3px;
  font-size: 10px;
}

.cave-farm-facility-card .fac-alloc-controls {
  gap: 8px;
}

.cave-farm-facility-card .alloc-btn {
  width: 27px;
  height: 27px;
  border-width: 1px;
  font-size: 17px;
}

.cave-farm-facility-card .alloc-value {
  color: #6b8159;
  font-size: 19px;
}

.cave-farm-facility-card .fac-effect-text {
  overflow: hidden;
  color: #907c59;
  font-size: 10px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.cave-detail-area {
  min-width: 0;
  background: #f8f3e7;
}

.cave-detail-area .facility-panel {
  height: 100%;
  overflow: auto;
  padding: 14px;
  background: #f8f3e7;
}

.cave-detail-area .welcome-panel {
  min-height: 100%;
  background: #f8f3e7;
}

.cave-detail-area .welcome-panel .welcome-content {
  padding: 30px 22px;
}

.cave-detail-area .welcome-panel .welcome-content h2 {
  color: #59472e;
  font-family: "KaiTi", "STKaiti", serif;
}

.cave-detail-area .welcome-panel .welcome-content p {
  color: #917c59;
}

.cave-detail-area .welcome-panel .welcome-content .stats-overview {
  gap: 8px;
  flex-wrap: wrap;
}

.cave-detail-area .welcome-panel .welcome-content .stats-overview .stat-box {
  min-width: 110px;
  padding: 13px 15px;
  background: #fffaf0;
  border: 1px solid #ddcfaa;
  border-radius: 4px;
}

.cave-detail-area .welcome-panel .welcome-content .stats-overview .stat-box .stat-num {
  color: #66815a;
  font-size: 24px;
}

@media (max-width: 900px) {
  .cave-system-layout--farm {
    grid-template-columns: minmax(0, 1fr) minmax(360px, .95fr);
  }

  .cave-farm-facility-overview .facility-card-list {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 768px) {
  .cave-system-layout--farm {
    display: block;
    height: auto;
    min-height: 0;
    background: #f4f0e2;
  }

  .cave-farm-visual-panel {
    width: 100%;
    max-height: none;
    overflow: visible;
    padding: 0 10px 12px;
    border-right: none;
  }

  .cave-farm-header-bar {
    margin: 0 -10px;
    padding: 10px;
  }

  .cave-farm-header-bar .exp-text {
    display: block;
    margin: 3px 0 0;
  }

  .cave-farm-scene {
    min-height: 300px;
    aspect-ratio: 1.12;
  }

  .cave-farm-hotspot {
    min-width: 72px;
    padding: 4px 5px;
  }

  .cave-farm-hotspot strong {
    font-size: 10px;
  }

  .cave-farm-hotspot small {
    font-size: 8px;
  }

  .cave-farm-hotspot-icon {
    width: 25px;
    height: 25px;
  }

  .cave-hotspot-cultivation { left: 21%; top: 64%; }
  .cave-hotspot-alchemy { left: 72%; top: 27%; }
  .cave-hotspot-forging { left: 76%; top: 71%; }

  .cave-farm-scene-core {
    min-width: 108px;
    padding: 7px 8px;
  }

  .cave-farm-scene-core strong {
    max-width: 108px;
    font-size: 11px;
  }

  .cave-resource-strip {
    gap: 5px;
  }

  .cave-resource-item {
    gap: 4px;
    padding: 6px 5px;
  }

  .cave-resource-item strong {
    font-size: 11px;
  }

  .resource-icon {
    width: 23px;
    height: 23px;
  }

  .cave-detail-area {
    width: 100%;
    min-height: 0;
  }

  .cave-detail-area .facility-panel {
    height: auto;
    max-height: none;
    padding: 10px;
  }

  .cave-detail-area .welcome-panel {
    min-height: 360px;
  }
}

@media (max-width: 420px) {
  .cave-farm-scene {
    min-height: 270px;
  }

  .cave-farm-scene-title span {
    font-size: 16px;
  }

  .cave-resource-item small {
    font-size: 8px;
  }

  .cave-resource-item strong {
    font-size: 10px;
  }
}

/* 新农式单列洞府：场景、设置和设施内容统一在同一条阅读流中。 */
.cave-farm-page-shell {
  min-height: 650px;
  padding: 14px;
  color: #4f3f29;
  background: #f4f0e2;
}

.cave-farm-page-header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  background: #fffaf0;
  border: 1px solid #d4bd89;
  border-radius: 4px;
}

.cave-farm-page-header .cave-title-section {
  display: flex;
  align-items: center;
  flex: 1;
  min-width: 0;
  gap: 9px;
}

.cave-farm-page-header .cave-icon {
  display: grid;
  place-items: center;
  flex: 0 0 auto;
  width: 38px;
  height: 38px;
  color: #fff;
  background: #6d8f70;
  border: 2px solid #f4dfaa;
  border-radius: 50%;
}

.cave-farm-page-header h3 {
  margin: 0;
  color: #58452b;
  font: 900 19px/1.2 "KaiTi", "STKaiti", serif;
}

.cave-farm-page-header .cave-meta {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 3px;
}

.cave-farm-page-header .realm-text,
.cave-farm-page-header .exp-text {
  color: #967d57;
  font-size: 10px;
}

.cave-farm-page-header .exp-text {
  margin-left: 2px;
}

.cave-farm-scene {
  min-height: 370px;
  margin: 12px 0;
}

.cave-hotspot-settings {
  left: 50%;
  top: 26%;
  transform: translateX(-50%);
}

.cave-hotspot-settings:hover,
.cave-hotspot-settings.active {
  transform: translate(-50%, -2px);
}

.cave-settings-icon {
  background: #92734b;
}

.cave-settings-panel {
  padding: 12px;
  background: #fffaf0;
  border: 1px solid #d8c99e;
  border-radius: 4px;
  box-shadow: 0 4px 12px rgba(83, 68, 38, .08);
}

.cave-settings-heading {
  display: flex;
  align-items: end;
  justify-content: space-between;
  gap: 10px;
  padding-bottom: 9px;
  border-bottom: 1px solid #e3d8bb;
}

.cave-settings-heading div {
  display: grid;
  gap: 3px;
}

.cave-settings-heading div span {
  color: #654a2e;
  font: 900 17px/1.2 "KaiTi", "STKaiti", serif;
}

.cave-settings-heading strong {
  color: #998460;
  font-size: 10px;
  font-weight: 400;
}

.cave-slot-total {
  color: #66815a;
  font-size: 13px;
  font-weight: 800;
  white-space: nowrap;
}

.cave-settings-panel .cave-resource-strip {
  margin: 10px 0;
}

.cave-settings-panel .cave-farm-facility-overview {
  border: 0;
  box-shadow: none;
}

.cave-facility-tabs {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 7px;
  margin: 12px 0 10px;
}

.cave-facility-tab {
  display: grid;
  justify-items: center;
  gap: 3px;
  min-width: 0;
  padding: 8px 5px;
  color: #806a48;
  background: #f4ead1;
  border: 1px solid #d5c194;
  border-radius: 4px;
  cursor: pointer;
}

.cave-facility-tab:hover,
.cave-facility-tab.active {
  color: #fff8dd;
  background: #6b8f70;
  border-color: #54765b;
}

.cave-facility-tab span:not(.cave-facility-tab-icon) {
  overflow: hidden;
  max-width: 100%;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 12px;
  font-weight: 800;
}

.cave-facility-tab small {
  overflow: hidden;
  max-width: 100%;
  color: #a18b68;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 9px;
}

.cave-facility-tab.active small {
  color: #e8dcaf;
}

.cave-facility-tab-icon {
  display: grid;
  place-items: center;
  width: 28px;
  height: 28px;
  color: #fff;
  border: 1px solid #ffe5a0;
  border-radius: 50%;
}

.cave-settings-tab .cave-facility-tab-icon {
  background: #92734b;
}

.cave-facility-stage {
  position: relative;
  min-height: 420px;
  overflow: hidden;
  padding: 12px;
  background-position: center;
  background-size: cover;
  border: 1px solid #a88b58;
  border-radius: 5px;
  box-shadow: inset 0 0 0 1px rgba(255, 247, 212, .38), 0 10px 24px rgba(73, 64, 42, .13);
}

.cave-facility-stage-shade {
  position: absolute;
  inset: 0;
  background: rgba(30, 33, 26, .28);
  pointer-events: none;
}

.cave-facility-stage-header,
.cave-facility-stage-content {
  position: relative;
  z-index: 1;
}

.cave-facility-stage-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 9px;
  padding: 7px 9px;
  color: #fff4c9;
  background: rgba(35, 45, 37, .72);
  border-left: 3px solid #e1bf63;
  text-shadow: 0 1px 2px #1b251e;
}

.cave-facility-stage-header span {
  font: 900 17px/1 "KaiTi", "STKaiti", serif;
}

.cave-facility-stage-header small {
  color: #e6d7a5;
  font-size: 10px;
}

.farm-child-panel {
  padding: 0;
  background: transparent;
}

.cave-settings-hint {
  padding: 16px;
  background: rgba(255, 250, 240, .9);
  border: 1px solid #d8c99e;
  border-radius: 4px;
  text-align: center;
}

.cave-settings-hint .welcome-content {
  display: grid;
  justify-items: center;
  gap: 5px;
}

.cave-settings-hint h2 {
  margin: 0;
  color: #654a2e;
  font: 900 18px/1.2 "KaiTi", "STKaiti", serif;
}

.cave-settings-hint p,
.cave-settings-hint span {
  margin: 0;
  color: #9a8463;
  font-size: 10px;
}

@media (max-width: 768px) {
  .cave-farm-page-shell {
    min-height: 0;
    padding: 10px;
  }

  .cave-farm-page-header {
    align-items: flex-start;
    flex-wrap: wrap;
    padding: 9px;
  }

  .cave-farm-page-header > .ivu-btn {
    margin-left: 47px;
  }

  .cave-farm-page-header .exp-text {
    display: block;
    width: 100%;
    margin-left: 0;
  }

  .cave-farm-scene {
    min-height: 300px;
    aspect-ratio: 1.1;
  }

  .cave-facility-tabs {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .cave-facility-stage {
    min-height: 0;
    padding: 9px;
  }

  .cave-facility-stage-header {
    align-items: flex-start;
    flex-direction: column;
  }
}

.cave-farm-page-shell {
  min-height: 650px;
  padding: 14px;
  color: #4f3f29;
  background: #f4f0e2;
}

.cave-farm-page-header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  background: #fffaf0;
  border: 1px solid #d4bd89;
  border-radius: 4px;
}

.cave-farm-page-header .cave-title-section {
  display: flex;
  align-items: center;
  flex: 1;
  min-width: 0;
  gap: 9px;
}

.cave-farm-page-header .cave-icon {
  display: grid;
  place-items: center;
  flex: 0 0 auto;
  width: 38px;
  height: 38px;
  color: #fff;
  background: #6d8f70;
  border: 2px solid #f4dfaa;
  border-radius: 50%;
}

.cave-farm-page-header h3 {
  margin: 0;
  color: #58452b;
  font: 900 19px/1.2 "KaiTi", "STKaiti", serif;
}

.cave-farm-page-header .cave-meta {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 3px;
}

.cave-farm-page-header .realm-text,
.cave-farm-page-header .exp-text {
  color: #967d57;
  font-size: 10px;
}

.cave-farm-page-header .exp-text {
  margin-left: 2px;
}

.cave-farm-scene {
  min-height: 370px;
  margin: 12px 0;
}

.cave-hotspot-settings {
  left: 50%;
  top: 26%;
  transform: translateX(-50%);
}

.cave-hotspot-settings:hover,
.cave-hotspot-settings.active {
  transform: translate(-50%, -2px);
}

.cave-settings-icon {
  background: #92734b;
}

.cave-settings-panel {
  padding: 12px;
  background: #fffaf0;
  border: 1px solid #d8c99e;
  border-radius: 4px;
  box-shadow: 0 4px 12px rgba(83, 68, 38, .08);
}

.cave-settings-heading {
  display: flex;
  align-items: end;
  justify-content: space-between;
  gap: 10px;
  padding-bottom: 9px;
  border-bottom: 1px solid #e3d8bb;
}

.cave-settings-heading div {
  display: grid;
  gap: 3px;
}

.cave-settings-heading div span {
  color: #654a2e;
  font: 900 17px/1.2 "KaiTi", "STKaiti", serif;
}

.cave-settings-heading strong {
  color: #998460;
  font-size: 10px;
  font-weight: 400;
}

.cave-slot-total {
  color: #66815a;
  font-size: 13px;
  font-weight: 800;
  white-space: nowrap;
}

.cave-settings-panel .cave-resource-strip {
  margin: 10px 0;
}

.cave-settings-panel .cave-farm-facility-overview {
  border: 0;
  box-shadow: none;
}

.cave-facility-tabs {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 7px;
  margin: 12px 0 10px;
}

.cave-facility-tab {
  display: grid;
  justify-items: center;
  gap: 3px;
  min-width: 0;
  padding: 8px 5px;
  color: #806a48;
  background: #f4ead1;
  border: 1px solid #d5c194;
  border-radius: 4px;
  cursor: pointer;
}

.cave-facility-tab:hover,
.cave-facility-tab.active {
  color: #fff8dd;
  background: #6b8f70;
  border-color: #54765b;
}

.cave-facility-tab span:not(.cave-facility-tab-icon) {
  overflow: hidden;
  max-width: 100%;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 12px;
  font-weight: 800;
}

.cave-facility-tab small {
  overflow: hidden;
  max-width: 100%;
  color: #a18b68;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 9px;
}

.cave-facility-tab.active small {
  color: #e8dcaf;
}

.cave-facility-tab-icon {
  display: grid;
  place-items: center;
  width: 28px;
  height: 28px;
  color: #fff;
  border: 1px solid #ffe5a0;
  border-radius: 50%;
}

.cave-settings-tab .cave-facility-tab-icon {
  background: #92734b;
}

.cave-facility-stage {
  position: relative;
  min-height: 420px;
  overflow: hidden;
  padding: 12px;
  background-position: center;
  background-size: cover;
  border: 1px solid #a88b58;
  border-radius: 5px;
  box-shadow: inset 0 0 0 1px rgba(255, 247, 212, .38), 0 10px 24px rgba(73, 64, 42, .13);
}

.cave-facility-stage-shade {
  position: absolute;
  inset: 0;
  background: rgba(30, 33, 26, .28);
  pointer-events: none;
}

.cave-facility-stage-header,
.cave-facility-stage-content {
  position: relative;
  z-index: 1;
}

.cave-facility-stage-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 9px;
  padding: 7px 9px;
  color: #fff4c9;
  background: rgba(35, 45, 37, .72);
  border-left: 3px solid #e1bf63;
  text-shadow: 0 1px 2px #1b251e;
}

.cave-facility-stage-header span {
  font: 900 17px/1 "KaiTi", "STKaiti", serif;
}

.cave-facility-stage-header small {
  color: #e6d7a5;
  font-size: 10px;
}

.farm-child-panel {
  padding: 0;
  background: transparent;
}

.cave-settings-hint {
  padding: 16px;
  background: rgba(255, 250, 240, .9);
  border: 1px solid #d8c99e;
  border-radius: 4px;
  text-align: center;
}

.cave-settings-hint .welcome-content {
  display: grid;
  justify-items: center;
  gap: 5px;
}

.cave-settings-hint h2 {
  margin: 0;
  color: #654a2e;
  font: 900 18px/1.2 "KaiTi", "STKaiti", serif;
}

.cave-settings-hint p,
.cave-settings-hint span {
  margin: 0;
  color: #9a8463;
  font-size: 10px;
}

@media (max-width: 768px) {
  .cave-farm-page-shell {
    min-height: 0;
    padding: 10px;
  }

  .cave-farm-page-header {
    align-items: flex-start;
    flex-wrap: wrap;
    padding: 9px;
  }

  .cave-farm-page-header > .ivu-btn {
    margin-left: 47px;
  }

  .cave-farm-page-header .exp-text {
    display: block;
    width: 100%;
    margin-left: 0;
  }

  .cave-farm-scene {
    min-height: 300px;
    aspect-ratio: 1.1;
  }

  .cave-facility-tabs {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .cave-facility-stage {
    min-height: 0;
    padding: 9px;
  }

  .cave-facility-stage-header {
    align-items: flex-start;
    flex-direction: column;
  }
}

.cave-farm-fullscreen {
  min-height: calc(70vh - 24px);
  padding: 0;
  background: #25352b;
}

.cave-overview-screen,
.cave-farm-subpage {
  position: relative;
  min-height: calc(80vh - 24px);
  overflow: hidden;
  background-position: center;
  background-size: cover;
}

.cave-overview-screen {
  height: auto;
  min-height: 360px;
  aspect-ratio: 1.5;
}

/* 白蓝主题覆盖层：保留上方经典样式，便于通过 caveTheme 快速回退。 */
.cave-theme-blue {
  --cave-blue-bg: #f4f8fc;
  --cave-blue-panel: #ffffff;
  --cave-blue-soft: #f8fbfe;
  --cave-blue-border: #c9d9e8;
  --cave-blue-border-soft: #e1eaf2;
  --cave-blue-title: #233b55;
  --cave-blue-text: #506b84;
  --cave-blue-muted: #8298aa;
  --cave-blue-main: #3978b7;
  --cave-blue-main-dark: #2f669d;
  --cave-blue-selected: #ddebf8;
}

.cave-theme-blue .cave-overview-topbar,
.cave-theme-blue .cave-subpage-header {
  color: #edf6ff;
  background: rgba(35, 59, 85, .88);
  border-color: rgba(183, 214, 239, .7);
  border-left-color: #8fb8dc;
}

.cave-theme-blue .cave-overview-topbar h3,
.cave-theme-blue .cave-subpage-header h2 { color: #f5faff; }
.cave-theme-blue .cave-overview-topbar p,
.cave-theme-blue .cave-subpage-header p,
.cave-theme-blue .cave-subpage-header span { color: #c5dced; }
.cave-theme-blue .cave-overview-topbar .cave-icon { background: #3978b7; border-color: #b7d5ee; }
.cave-theme-blue .cave-overview-resource { color: #dcecf9; background: rgba(35, 59, 85, .9); border-color: rgba(183, 214, 239, .7); }
.cave-theme-blue .cave-overview-resource b { color: #f5faff; }
.cave-theme-blue .cave-scene-hotspot { color: #f1f8ff; background: rgba(35, 59, 85, .84); border-color: rgba(183, 214, 239, .82); }
.cave-theme-blue .cave-scene-hotspot:hover { background: rgba(57, 120, 183, .94); }
.cave-theme-blue .cave-scene-hotspot small { color: #c5dced; }
.cave-theme-blue .cave-scene-hotspot-glyph { border-color: #c4ddf1; }
.cave-theme-blue .cave-scene-hotspot-glyph.market-hotspot-glyph { background: #6f8eaa; }
.cave-theme-blue .cave-scene-hotspot-glyph.aura-hotspot-glyph { background: #6e85ad; }
.cave-theme-blue .cave-scene-hotspot-icon.settings { background: #6685a2; }

.cave-theme-blue .cave-subpage-content,
.cave-theme-blue .cave-settings-page {
  background: rgba(255, 255, 255, .94);
  border-color: var(--cave-blue-border);
  box-shadow: 0 14px 32px rgba(43, 79, 111, .16);
}
.cave-theme-blue .cave-settings-page .cave-settings-heading,
.cave-theme-blue .cave-settings-footer { border-color: var(--cave-blue-border-soft); }
.cave-theme-blue .cave-settings-page .cave-settings-heading span { color: var(--cave-blue-title); }
.cave-theme-blue .cave-settings-page .cave-settings-heading strong,
.cave-theme-blue .cave-settings-footer { color: var(--cave-blue-muted); }
.cave-theme-blue .cave-settings-page .cave-settings-heading > b,
.cave-theme-blue .cave-time-setting-row > b { color: var(--cave-blue-main); }
.cave-theme-blue .cave-settings-page .cave-resource-item,
.cave-theme-blue .cave-time-setting-row { color: var(--cave-blue-text); background: var(--cave-blue-soft); border-color: var(--cave-blue-border-soft); }
.cave-theme-blue .cave-settings-page .cave-resource-item small,
.cave-theme-blue .cave-time-setting-row small { color: var(--cave-blue-muted); }
.cave-theme-blue .cave-settings-page .cave-resource-item strong { color: var(--cave-blue-main); }
.cave-theme-blue .cave-time-setting-glyph { border-color: #b7d5ee; box-shadow: inset 0 0 0 2px rgba(255, 255, 255, .35), 0 2px 5px rgba(55, 100, 140, .18); }

.cave-theme-blue >>> .ivu-btn,
.cave-theme-blue >>> .ivu-input,
.cave-theme-blue >>> .ivu-input-number,
.cave-theme-blue >>> .ivu-select-selection {
  color: #426581;
  background: #f7fbff;
  border-color: #b9cde0;
  box-shadow: none;
}
.cave-theme-blue >>> .ivu-btn:hover,
.cave-theme-blue >>> .ivu-input:hover,
.cave-theme-blue >>> .ivu-select-selection:hover { color: #315a7c; background: #eef6fd; border-color: #7fa9ca; }
.cave-theme-blue >>> .ivu-btn-primary,
.cave-theme-blue >>> .ivu-btn-success { color: #fff; background: #3978b7; border-color: #2f669d; }
.cave-theme-blue >>> .ivu-btn-primary:hover,
.cave-theme-blue >>> .ivu-btn-success:hover { color: #fff; background: #2f669d; border-color: #285783; }
.cave-theme-blue >>> .ivu-btn-warning { color: #fff; background: #c29352; border-color: #a9783a; }
.cave-theme-blue >>> .ivu-btn-warning:hover { color: #fff; background: #aa7b3e; border-color: #8f642f; }
.cave-theme-blue >>> .ivu-btn-error { color: #fff; background: #b86d70; border-color: #9f565a; }
.cave-theme-blue >>> .ivu-btn-error:hover { color: #fff; background: #a75d61; border-color: #8d494d; }
.cave-theme-blue .alloc-btn { color: var(--cave-blue-main); background: #e8f2fb; border-color: #8eabc5; }
.cave-theme-blue .alloc-btn:hover:not(:disabled) { color: #fff; background: var(--cave-blue-main); border-color: var(--cave-blue-main-dark); }
.cave-theme-blue .alloc-btn.minus { color: #b86d70; background: #f9ecee; border-color: #d39a9d; }
.cave-theme-blue .alloc-btn.minus:hover:not(:disabled) { color: #fff; background: #b86d70; border-color: #9f565a; }

/* 二级设施组件共用白蓝皮肤。 */
.cave-theme-blue >>> .farm-facility-page,
.cave-theme-blue >>> .farm-market-page,
.cave-theme-blue >>> .farm-aura-page { color: var(--cave-blue-text); background: var(--cave-blue-bg); }
.cave-theme-blue >>> .farm-facility-header,
.cave-theme-blue >>> .farm-market-tabs,
.cave-theme-blue >>> .farm-market-panel,
.cave-theme-blue >>> .farm-facility-panel,
.cave-theme-blue >>> .forge-panel,
.cave-theme-blue >>> .farm-recipe-list,
.cave-theme-blue >>> .farm-recipe-detail,
.cave-theme-blue >>> .farm-queue-panel,
.cave-theme-blue >>> .farm-aura-page,
.cave-theme-blue >>> .aura-filter-panel,
.cave-theme-blue >>> .aura-layout { color: var(--cave-blue-text); background: var(--cave-blue-panel); border-color: var(--cave-blue-border); }
.cave-theme-blue >>> .farm-facility-title h3,
.cave-theme-blue >>> .farm-market-heading h3,
.cave-theme-blue >>> .farm-detail-heading h4,
.cave-theme-blue >>> .aura-page-toolbar h3,
.cave-theme-blue >>> .aura-name-line h3 { color: var(--cave-blue-title); }
.cave-theme-blue >>> .farm-facility-title p,
.cave-theme-blue >>> .farm-facility-stats,
.cave-theme-blue >>> .farm-market-heading > strong,
.cave-theme-blue >>> .farm-market-row-desc,
.cave-theme-blue >>> .farm-panel-kicker,
.cave-theme-blue >>> .aura-page-toolbar p,
.cave-theme-blue >>> .aura-count { color: var(--cave-blue-muted); }
.cave-theme-blue >>> .farm-facility-tabs button,
.cave-theme-blue >>> .farm-section-list button,
.cave-theme-blue >>> .forge-sub-tabs button,
.cave-theme-blue >>> .farm-market-tabs button,
.cave-theme-blue >>> .farm-treasure-tabs button { color: #426581; background: #f7fbff; border-color: #b9cde0; }
.cave-theme-blue >>> .farm-facility-tabs button.active,
.cave-theme-blue >>> .farm-section-list button.active,
.cave-theme-blue >>> .forge-sub-tabs button.active,
.cave-theme-blue >>> .farm-market-tabs button.active,
.cave-theme-blue >>> .farm-treasure-tabs button.active { color: #fff; background: var(--cave-blue-main); border-color: var(--cave-blue-main-dark); }
.cave-theme-blue >>> .forge-item-list,
.cave-theme-blue >>> .forge-detail,
.cave-theme-blue >>> .farm-recipe-row,
.cave-theme-blue >>> .forge-item-row,
.cave-theme-blue >>> .farm-material-row,
.cave-theme-blue >>> .farm-queue-row,
.cave-theme-blue >>> .farm-market-row,
.cave-theme-blue >>> .aura-list-item,
.cave-theme-blue >>> .aura-attribute,
.cave-theme-blue >>> .aura-skill,
.cave-theme-blue >>> .aura-bag-row,
.cave-theme-blue >>> .aura-enhance-card { color: var(--cave-blue-text); background: var(--cave-blue-soft); border-color: var(--cave-blue-border-soft); }
.cave-theme-blue >>> .forge-item-row.active,
.cave-theme-blue >>> .farm-recipe-row.active,
.cave-theme-blue >>> .aura-list-item:hover,
.cave-theme-blue >>> .aura-list-item.active { color: var(--cave-blue-title); background: var(--cave-blue-selected); border-color: #5b91c1; }
.cave-theme-blue >>> .farm-material-row.enough { background: #eef7f1; border-color: #a5c9b1; }
.cave-theme-blue >>> .farm-market-row:hover { background: #eef6fd; border-color: #7fa9ca; }
.cave-theme-blue >>> .farm-market-row-facts,
.cave-theme-blue >>> .farm-recipe-row small,
.cave-theme-blue >>> .forge-item-row small,
.cave-theme-blue >>> .aura-card-status,
.cave-theme-blue >>> .aura-attribute span { color: var(--cave-blue-muted); }
.cave-theme-blue >>> .farm-market-row-facts b,
.cave-theme-blue >>> .forge-item-row em,
.cave-theme-blue >>> .farm-detail-heading > strong,
.cave-theme-blue >>> .recipe-meta,
.cave-theme-blue >>> .aura-attribute strong { color: var(--cave-blue-main); }
.cave-theme-blue >>> .farm-action-footer,
.cave-theme-blue >>> .farm-market-row-actions .ivu-btn,
.cave-theme-blue >>> .aura-action-row .ivu-btn { border-color: #b9cde0; }
.cave-theme-blue >>> .aura-page-toolbar { background: #fff; border-color: var(--cave-blue-border); }
.cave-theme-blue >>> .aura-list,
.cave-theme-blue >>> .aura-right { scrollbar-color: #8eabc5 #eaf1f7; }
.cave-theme-blue >>> .aura-list::-webkit-scrollbar-track,
.cave-theme-blue >>> .aura-right::-webkit-scrollbar-track { background: #eaf1f7; }
.cave-theme-blue >>> .aura-list::-webkit-scrollbar-thumb,
.cave-theme-blue >>> .aura-right::-webkit-scrollbar-thumb { background: #8eabc5; border-color: #eaf1f7; }
.cave-theme-blue >>> .aura-list::-webkit-scrollbar-thumb:hover,
.cave-theme-blue >>> .aura-right::-webkit-scrollbar-thumb:hover { background: #6489a8; }
.cave-theme-blue >>> .aura-list-icon { border-color: #8eabc5; box-shadow: 0 2px 7px rgba(55, 100, 140, .2), inset 0 0 0 2px rgba(255, 255, 255, .7); }
.cave-theme-blue >>> .aura-card-grade { border-color: #9dbbd4; }
.cave-theme-blue >>> .aura-card-follow,
.cave-theme-blue >>> .detail-follow { color: #9aaaba; }
.cave-theme-blue >>> .aura-card-follow.active,
.cave-theme-blue >>> .detail-follow.active { color: #d56a76; }
.cave-theme-blue >>> .aura-story,
.cave-theme-blue >>> .aura-skill-detail { color: var(--cave-blue-text); background: #f4f8fc; border-color: var(--cave-blue-border-soft); }
.cave-theme-blue >>> .aura-section-title { color: var(--cave-blue-title); border-left-color: var(--cave-blue-main); }
.cave-theme-blue >>> .aura-skill.active { color: var(--cave-blue-title); background: var(--cave-blue-selected); border-color: #5b91c1; }

/* 组件专用区域的最终覆盖，保证白蓝主题不被子组件旧卡片样式覆盖。 */
.cave-theme-blue .cave-subpage-header > .ivu-btn {
  color: #fff !important;
  background: #3978b7 !important;
  border-color: #2f669d !important;
  box-shadow: 0 2px 5px rgba(39, 83, 122, .2) !important;
}
.cave-theme-blue .cave-subpage-header > .ivu-btn:hover {
  color: #fff !important;
  background: #2f669d !important;
  border-color: #285783 !important;
}

.cave-theme-blue >>> .forge-panel,
.cave-theme-blue >>> .forge-choice-block,
.cave-theme-blue >>> .forge-detail,
.cave-theme-blue >>> .forge-item-list,
.cave-theme-blue >>> .farm-recipe-detail,
.cave-theme-blue >>> .farm-recipe-list,
.cave-theme-blue >>> .farm-facility-panel {
  background: #fff !important;
  border-color: #c9d9e8 !important;
  color: #506b84 !important;
}
.cave-theme-blue >>> .forge-choice-block,
.cave-theme-blue >>> .forge-detail-metrics,
.cave-theme-blue >>> .farm-recipe-metrics,
.cave-theme-blue >>> .breakthrough-summary {
  background: #f4f8fc !important;
  border-color: #c9d9e8 !important;
}
.cave-theme-blue >>> .forge-choice-title,
.cave-theme-blue >>> .forge-detail-metrics b,
.cave-theme-blue >>> .farm-recipe-metrics b,
.cave-theme-blue >>> .breakthrough-summary .farm-panel-kicker {
  color: #233b55 !important;
}
.cave-theme-blue >>> .forge-info-row {
  color: #506b84 !important;
  border-bottom-color: #e1eaf2 !important;
}
.cave-theme-blue >>> .forge-info-row > strong,
.cave-theme-blue >>> .forge-detail-metrics span,
.cave-theme-blue >>> .farm-recipe-metrics span {
  color: #506b84 !important;
}
.cave-theme-blue >>> .forge-detail-metrics strong,
.cave-theme-blue >>> .farm-recipe-metrics strong,
.cave-theme-blue >>> .breakthrough-summary strong,
.cave-theme-blue >>> .breakthrough-requirements b {
  color: #3978b7 !important;
}
.cave-theme-blue >>> .forge-detail-metrics i,
.cave-theme-blue >>> .farm-recipe-metrics i,
.cave-theme-blue >>> .farm-detail-heading > strong {
  color: #5b91c1 !important;
}
.cave-theme-blue >>> .farm-recipe-metrics,
.cave-theme-blue >>> .forge-detail-metrics {
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, .8) !important;
}

.cave-theme-blue >>> .breakthrough-requirements p {
  color: #506b84 !important;
  background: #fff !important;
  border-bottom-color: #e1eaf2 !important;
}
.cave-theme-blue >>> .breakthrough-summary small,
.cave-theme-blue >>> .farm-facility-note,
.cave-theme-blue >>> .forge-action-summary,
.cave-theme-blue >>> .forge-equipment-summary,
.cave-theme-blue >>> .forge-loading-state {
  color: #8298aa !important;
}
.cave-theme-blue >>> .forge-info-row .insufficient,
.cave-theme-blue >>> .farm-material-row strong {
  color: #b86d70 !important;
}
.cave-theme-blue >>> .farm-material-row.enough strong,
.cave-theme-blue >>> .farm-material-row.enough b {
  color: #5b9a78 !important;
}

.cave-theme-blue >>> .farm-list-toolbar .ivu-input,
.cave-theme-blue >>> .farm-list-toolbar .ivu-select-selection,
.cave-theme-blue >>> .forge-equipment-search .ivu-input,
.cave-theme-blue >>> .forge-choice-block .ivu-input-number,
.cave-theme-blue >>> .forge-choice-block .ivu-select-selection,
.cave-theme-blue >>> .farm-recipe-detail .ivu-input-number {
  color: #426581 !important;
  background: #fff !important;
  border-color: #b9cde0 !important;
  box-shadow: none !important;
}
.cave-theme-blue >>> .farm-list-toolbar .ivu-input:hover,
.cave-theme-blue >>> .farm-list-toolbar .ivu-input:focus,
.cave-theme-blue >>> .farm-list-toolbar .ivu-select-selection:hover,
.cave-theme-blue >>> .forge-equipment-search .ivu-input:hover,
.cave-theme-blue >>> .forge-equipment-search .ivu-input:focus,
.cave-theme-blue >>> .forge-choice-block .ivu-input-number:hover,
.cave-theme-blue >>> .forge-choice-block .ivu-select-selection:hover,
.cave-theme-blue >>> .farm-recipe-detail .ivu-input-number:hover {
  border-color: #7fa9ca !important;
  background: #f8fbfe !important;
}

/* 洞府统一按钮色：保留不同操作的语义色，但去掉默认蓝色主题。 */
.cave-overview-topbar >>> .ivu-btn,
.cave-subpage-header >>> .ivu-btn,
.cave-settings-footer >>> .ivu-btn,
.cave-system-container >>> .cave-upgrade-content .ivu-btn {
  color: #765a38;
  background: #f5ead1;
  border-color: #d6c092;
  box-shadow: none;
}

.cave-overview-topbar >>> .ivu-btn:hover,
.cave-subpage-header >>> .ivu-btn:hover,
.cave-settings-footer >>> .ivu-btn:hover,
.cave-system-container >>> .cave-upgrade-content .ivu-btn:hover {
  color: #624725;
  background: #f8efdd;
  border-color: #b58a52;
}

.cave-overview-topbar >>> .ivu-btn-warning,
.cave-subpage-header >>> .ivu-btn-warning,
.cave-settings-footer >>> .ivu-btn-warning,
.cave-system-container >>> .cave-upgrade-content .ivu-btn-primary {
  color: #fff8df;
  background: #a67842;
  border-color: #865d31;
}

.cave-overview-topbar >>> .ivu-btn-warning:hover,
.cave-subpage-header >>> .ivu-btn-warning:hover,
.cave-settings-footer >>> .ivu-btn-warning:hover,
.cave-system-container >>> .cave-upgrade-content .ivu-btn-primary:hover {
  color: #fff8df;
  background: #906c3b;
  border-color: #714d29;
}

.cave-farm-subpage >>> .ivu-btn,
.cave-system-container >>> .ivu-modal .ivu-btn {
  color: #765a38;
  background: #f5ead1;
  border-color: #d6c092;
  box-shadow: none;
}

.cave-farm-subpage >>> .ivu-btn:hover,
.cave-system-container >>> .ivu-modal .ivu-btn:hover {
  color: #624725;
  background: #f8efdd;
  border-color: #b58a52;
}

.cave-farm-subpage >>> .ivu-btn-primary,
.cave-farm-subpage >>> .ivu-btn-success,
.cave-system-container >>> .ivu-modal .ivu-btn-primary,
.cave-system-container >>> .ivu-modal .ivu-btn-success {
  color: #fff8df;
  background: #6b8f70;
  border-color: #54765b;
}

.cave-farm-subpage >>> .ivu-btn-primary:hover,
.cave-farm-subpage >>> .ivu-btn-success:hover,
.cave-system-container >>> .ivu-modal .ivu-btn-primary:hover,
.cave-system-container >>> .ivu-modal .ivu-btn-success:hover {
  color: #fff8df;
  background: #5d8064;
  border-color: #496c50;
}

.cave-farm-subpage >>> .ivu-btn-warning,
.cave-system-container >>> .ivu-modal .ivu-btn-warning {
  color: #fff8df;
  background: #a67842;
  border-color: #865d31;
}

.cave-farm-subpage >>> .ivu-btn-warning:hover,
.cave-system-container >>> .ivu-modal .ivu-btn-warning:hover {
  color: #fff8df;
  background: #906c3b;
  border-color: #714d29;
}

.cave-farm-subpage >>> .ivu-btn-error,
.cave-system-container >>> .ivu-modal .ivu-btn-error {
  color: #fff7e3;
  background: #a66c4d;
  border-color: #875338;
}

.cave-farm-subpage >>> .ivu-btn-error:hover,
.cave-system-container >>> .ivu-modal .ivu-btn-error:hover {
  color: #fff7e3;
  background: #915a3e;
  border-color: #73432f;
}

.cave-overview-shade,
.cave-subpage-shade {
  position: absolute;
  inset: 0;
  background: rgba(21, 32, 25, .2);
  pointer-events: none;
}

.cave-overview-topbar {
  position: absolute;
  z-index: 3;
  top: 16px;
  left: 16px;
  right: 16px;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  color: #fff5cf;
  background: rgba(30, 43, 34, .73);
  border: 1px solid rgba(255, 225, 154, .62);
  border-radius: 5px;
  text-shadow: 0 1px 2px #18231b;
}

.cave-overview-topbar .cave-title-section {
  display: flex;
  align-items: center;
  flex: 1;
  min-width: 0;
  gap: 9px;
}

.cave-overview-topbar .cave-icon {
  display: grid;
  place-items: center;
  flex: 0 0 auto;
  width: 38px;
  height: 38px;
  color: #fff3bf;
  background: #6d8f70;
  border: 1px solid #ffe4a2;
  border-radius: 50%;
}

.cave-overview-topbar h3 {
  margin: 0;
  color: #fff5cf;
  font: 900 20px/1.2 "KaiTi", "STKaiti", serif;
}

.cave-overview-topbar p {
  margin: 3px 0 0;
  color: #e9dcae;
  font-size: 10px;
}

.cave-overview-title {
  position: absolute;
  z-index: 2;
  left: 50%;
  top: 18%;
  display: grid;
  justify-items: center;
  gap: 4px;
  color: #fff5ca;
  text-align: center;
  text-shadow: 0 2px 4px #17251d;
  transform: translateX(-50%);
}

.cave-overview-title span {
  font: 900 30px/1 "KaiTi", "STKaiti", serif;
}

.cave-overview-title small {
  color: #f1e2b5;
  font-size: 11px;
}

.cave-scene-hotspot {
  position: absolute;
  z-index: 3;
  display: grid;
  justify-items: center;
  gap: 3px;
  min-width: 106px;
  padding: 7px 9px;
  color: #fff8d9;
  background: rgba(35, 52, 41, .78);
  border: 1px solid rgba(255, 229, 158, .84);
  border-radius: 5px;
  cursor: pointer;
  text-shadow: 0 1px 2px #17251d;
  transition: transform .18s ease, background .18s ease, box-shadow .18s ease;
}

.cave-scene-hotspot:hover {
  background: rgba(113, 79, 31, .9);
  box-shadow: 0 8px 18px rgba(25, 29, 20, .28);
  transform: translateY(-3px);
}

.cave-scene-hotspot strong {
  font-size: 13px;
  white-space: nowrap;
}

.cave-scene-hotspot small {
  color: #e8dbad;
  font-size: 10px;
}

.cave-scene-hotspot-icon {
  display: grid;
  place-items: center;
  width: 36px;
  height: 36px;
  color: #fff;
  border: 1px solid #ffe5a2;
  border-radius: 50%;
  font-size: 19px;
}

.cave-scene-hotspot-glyph {
  display: grid;
  place-items: center;
  width: 36px;
  height: 36px;
  color: #fff4bf;
  border: 2px solid #ffe6a0;
  border-radius: 50%;
  box-shadow: 0 2px 8px rgba(28, 37, 25, .3);
  font: 900 18px/1 "KaiTi", "STKaiti", serif;
}

.cave-scene-hotspot-icon.settings {
  background: #94754a;
}

.scene-facility-cultivation { left: 31%; top: 49%; }
.scene-facility-alchemy { left: 29%; top: 20%; }
.scene-facility-forging { left: 68%; top: 54%; }
.scene-facility-aura { left: 49%; top: 70%; }
.scene-facility-market { left: 76%; top: 20%; }
.cave-scene-hotspot-glyph.market-hotspot-glyph { background: #a66c4d; }
.cave-scene-hotspot-glyph.aura-hotspot-glyph { background: #8d6bb3; }
.cave-hotspot-settings { left: 50%; top: 29%; transform: translateX(-50%); }
.cave-hotspot-settings:hover { transform: translate(-50%, -3px); }

.cave-overview-core {
  position: absolute;
  z-index: 2;
  left: 50%;
  top: 53%;
  display: grid;
  justify-items: center;
  gap: 4px;
  min-width: 172px;
  padding: 13px 16px;
  color: #fff2bd;
  background: rgba(35, 51, 39, .67);
  border: 1px solid rgba(255, 226, 155, .7);
  border-radius: 5px;
  text-align: center;
  text-shadow: 0 1px 2px #16231b;
  transform: translate(-50%, -50%);
}

.cave-overview-core strong {
  max-width: 180px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font: 900 17px/1.2 "KaiTi", "STKaiti", serif;
}

.cave-overview-core span {
  color: #e7d9a8;
  font-size: 10px;
}

.cave-overview-resource {
  position: absolute;
  z-index: 3;
  right: 16px;
  bottom: 16px;
  display: flex;
  gap: 7px;
  padding: 8px;
  color: #f4e6b4;
  background: rgba(30, 43, 34, .78);
  border: 1px solid rgba(255, 225, 154, .62);
  border-radius: 4px;
  font-size: 10px;
}

.cave-overview-resource span {
  padding: 0 7px;
  border-right: 1px solid rgba(255, 231, 169, .32);
}

.cave-overview-resource span:last-child {
  border-right: 0;
}

.cave-overview-resource b {
  color: #fff5c7;
  font-size: 13px;
}

.cave-subpage {
  min-height: calc(80vh - 24px);
  padding: 16px;
}

.cave-subpage-header {
  position: relative;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 12px;
  color: #fff4c9;
  background: rgba(31, 43, 34, .78);
  border: 1px solid rgba(255, 226, 155, .68);
  border-left: 3px solid #e1bf63;
  border-radius: 4px;
  text-shadow: 0 1px 2px #1b251e;
}

.cave-subpage-header div {
  min-width: 0;
}

.cave-subpage-header span {
  color: #e6d6a2;
  font-size: 10px;
}

.cave-subpage-header h2 {
  margin: 3px 0;
  font: 900 22px/1.2 "KaiTi", "STKaiti", serif;
}

.cave-subpage-header p {
  margin: 0;
  color: #e7dbb0;
  font-size: 10px;
}

.cave-subpage-content,
.cave-settings-page {
  position: relative;
  z-index: 2;
  max-width: 980px;
  margin: 12px auto 0;
  padding: 12px;
  background: rgba(255, 249, 230, .9);
  border: 1px solid #d2b77e;
  border-radius: 5px;
  box-shadow: 0 14px 32px rgba(22, 28, 21, .22);
}

.cave-time-setting-list {
  display: grid;
  gap: 7px;
}

.cave-time-setting-row {
  display: grid;
  grid-template-columns: 34px minmax(0, 1fr) 30px 34px 30px;
  align-items: center;
  gap: 8px;
  padding: 9px;
  color: #654d31;
  background: #f8f0df;
  border: 1px solid #e1d1ae;
  border-radius: 4px;
}

.cave-time-setting-row > div {
  display: grid;
  gap: 2px;
}

.cave-time-setting-row strong {
  font-size: 12px;
}

.cave-time-setting-row small {
  color: #9a8462;
  font-size: 9px;
}

.cave-time-setting-row > b {
  color: #67825c;
  text-align: center;
}

.cave-time-setting-glyph {
  display: grid;
  place-items: center;
  width: 32px;
  height: 32px;
  color: #fff;
  font: 900 15px/1 "KaiTi", "STKaiti", serif;
  border: 1px solid #ffe4a2;
  border-radius: 50%;
  box-shadow: inset 0 0 0 2px rgba(255, 255, 255, .22), 0 2px 5px rgba(102, 75, 39, .22);
}

.cave-settings-page .cave-settings-heading {
  display: flex;
  align-items: end;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 10px;
  padding-bottom: 9px;
  border-bottom: 1px solid #e0d0ae;
}

.cave-settings-page .cave-settings-heading div {
  display: grid;
  gap: 3px;
}

.cave-settings-page .cave-settings-heading span {
  color: #654a2e;
  font: 900 19px/1.2 "KaiTi", "STKaiti", serif;
}

.cave-settings-page .cave-settings-heading strong {
  color: #998460;
  font-size: 10px;
  font-weight: 400;
}

.cave-settings-page .cave-settings-heading > b {
  color: #66815a;
  font-size: 13px;
  white-space: nowrap;
}

.cave-settings-page .cave-resource-strip {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 7px;
  margin-bottom: 10px;
}

.cave-settings-page .cave-resource-item {
  display: flex;
  align-items: center;
  gap: 7px;
  min-width: 0;
  padding: 8px;
  color: #55452e;
  background: #fffaf0;
  border: 1px solid #d8c99e;
  border-radius: 4px;
}

.cave-settings-page .cave-resource-item > span:last-child {
  display: grid;
  min-width: 0;
  gap: 2px;
}

.cave-settings-page .cave-resource-item small {
  color: #8c7957;
  font-size: 9px;
}

.cave-settings-page .cave-resource-item strong {
  color: #5b704e;
  font-size: 13px;
}

.cave-settings-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px solid #e0d0ae;
  color: #9a8463;
  font-size: 10px;
}

@media (max-width: 768px) {
  .cave-farm-fullscreen,
  .cave-overview-screen,
  .cave-farm-subpage {
    min-height: calc(92vh - 24px);
  }

  .cave-overview-screen {
    height: calc(92vh - 24px);
    aspect-ratio: auto;
  }

  .cave-overview-topbar {
    top: 10px;
    left: 10px;
    right: 10px;
    align-items: flex-start;
    flex-wrap: wrap;
    padding: 8px;
  }

  .cave-overview-topbar > .ivu-btn {
    margin-left: 47px;
  }

  .cave-overview-title {
    top: 19%;
  }

  .cave-overview-title span {
    font-size: 23px;
  }

  .cave-scene-hotspot {
    min-width: 78px;
    padding: 5px;
  }

  .cave-scene-hotspot strong {
    font-size: 10px;
  }

  .cave-scene-hotspot small {
    font-size: 8px;
  }

  .cave-scene-hotspot-icon {
    width: 28px;
    height: 28px;
    font-size: 15px;
  }

  .cave-scene-hotspot-glyph {
    width: 28px;
    height: 28px;
    font-size: 15px;
  }

  .scene-facility-cultivation { left: 30%; top: 49%; }
  .scene-facility-alchemy { left: 25%; top: 20%; }
  .scene-facility-forging { left: 70%; top: 54%; }
  .scene-facility-aura { left: 50%; top: 72%; }
  .scene-facility-market { left: 77%; top: 20%; }
  .cave-hotspot-settings { top: 39%; }

  .cave-overview-core {
    min-width: 130px;
    padding: 9px;
  }

  .cave-overview-core strong {
    max-width: 135px;
    font-size: 13px;
  }

  .cave-overview-core span {
    font-size: 8px;
  }

  .cave-overview-resource {
    right: 10px;
    bottom: 10px;
    left: 10px;
    justify-content: space-around;
    gap: 0;
    padding: 6px 3px;
  }

  .cave-overview-resource span {
    padding: 0 4px;
  }

  .cave-overview-resource b {
    font-size: 11px;
  }

  .cave-subpage {
    padding: 10px;
  }

  .cave-subpage-header {
    align-items: flex-start;
  }

  .cave-subpage-header h2 {
    font-size: 19px;
  }

  .cave-subpage-content,
  .cave-settings-page {
    margin-top: 9px;
    padding: 9px;
  }

  .cave-time-setting-row {
    grid-template-columns: 30px minmax(0, 1fr) 28px 28px 28px;
    gap: 5px;
    padding: 7px;
  }

  .cave-time-setting-glyph {
    width: 28px;
    height: 28px;
  }

  .cave-settings-page .cave-settings-heading {
    align-items: flex-start;
    flex-direction: column;
  }

  .cave-settings-page .cave-resource-strip {
    gap: 5px;
  }

  .cave-settings-page .cave-resource-item {
    gap: 4px;
    padding: 6px 4px;
  }

  .cave-settings-page .cave-resource-item strong {
    font-size: 11px;
  }
}

/* 升级入口的最终覆盖，确保主题色优先于设施子组件的默认按钮色。 */
.cave-theme-blue .cave-overview-topbar > .ivu-btn-warning,
.cave-theme-blue .cave-subpage-header > .ivu-btn-warning,
.cave-theme-blue >>> .farm-facility-header > .ivu-btn-warning {
  color: #fff !important;
  background: #3978b7 !important;
  border-color: #2f669d !important;
  box-shadow: 0 2px 5px rgba(39, 83, 122, .2) !important;
}

.cave-theme-blue .cave-overview-topbar > .ivu-btn-warning:hover,
.cave-theme-blue .cave-subpage-header > .ivu-btn-warning:hover,
.cave-theme-blue >>> .farm-facility-header > .ivu-btn-warning:hover {
  color: #fff !important;
  background: #2f669d !important;
  border-color: #285783 !important;
}

/* 移动端统一为单列阅读流，避免场景标题、按钮和子页面内容互相挤压。 */
@media (max-width: 768px) {
  .cave-farm-fullscreen {
    min-height: 0;
    background: #fff;
  }

  .cave-overview-screen,
  .cave-farm-subpage {
    min-height: 0;
  }

  .cave-overview-screen {
    height: calc(100vh - 16px);
    min-height: 520px;
  }

  .cave-overview-topbar {
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto;
    align-items: center;
    gap: 8px;
    padding: 7px;
  }

  .cave-overview-topbar > .ivu-btn {
    margin-left: 0;
    min-height: 30px;
    padding: 3px 7px;
    white-space: nowrap;
  }

  .cave-overview-topbar h3 {
    max-width: 100%;
    overflow: hidden;
    font-size: 17px;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .cave-overview-topbar p {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .cave-overview-title {
    top: 17%;
  }

  .cave-overview-title span {
    font-size: 21px;
  }

  .cave-scene-hotspot {
    min-width: 72px;
    max-width: 88px;
    padding: 5px 4px;
    border-radius: 5px;
  }

  .cave-scene-hotspot strong {
    max-width: 100%;
    overflow: hidden;
    font-size: 10px;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .cave-scene-hotspot small {
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .cave-overview-resource {
    right: 8px;
    bottom: 8px;
    left: 8px;
    gap: 0;
  }

  .cave-overview-resource span {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .cave-subpage {
    height: calc(100vh - 16px);
    padding: 8px;
    background-image: none !important;
    background: #fff;
  }

  .cave-subpage-shade {
    display: none;
  }

  .cave-subpage-header {
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto;
    align-items: center;
    gap: 8px;
    padding: 8px;
    color: #233b55;
    background: #f4f8fc;
    border: 1px solid #c9d9e8;
    border-radius: 5px;
    text-shadow: none;
  }

  .cave-subpage-header h2 {
    overflow: hidden;
    font-size: 18px;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .cave-subpage-header p {
    overflow: hidden;
    color: #8298aa;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .cave-subpage-header > .ivu-btn {
    min-height: 30px;
    padding: 3px 7px;
  }

  .cave-subpage-content,
  .cave-settings-page {
    max-width: none;
    max-height: calc(100vh - 82px);
    margin-top: 8px;
    padding: 8px;
    overflow-y: auto;
    background: #fff;
    border: 1px solid #c9d9e8;
    border-radius: 5px;
    box-shadow: none;
  }

  .cave-subpage-content .farm-child-panel,
  .cave-subpage-content >>> .farm-facility-page,
  .cave-subpage-content >>> .farm-market-page {
    min-width: 0;
  }

  .cave-time-setting-list {
    display: grid;
    gap: 5px;
  }

  .cave-time-setting-row {
    grid-template-columns: 30px minmax(0, 1fr) 28px 26px 28px;
    gap: 5px;
    padding: 6px;
  }

  .cave-settings-heading {
    gap: 5px;
  }

  .cave-resource-strip {
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 5px;
  }

  .cave-resource-item {
    min-width: 0;
    padding: 6px 4px;
  }

  .cave-resource-item strong {
    overflow: hidden;
    font-size: 11px;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .cave-settings-footer {
    align-items: flex-start;
    flex-direction: column;
  }

  .cave-settings-footer > .ivu-btn {
    width: 100%;
  }

  .cave-subpage-content >>> .farm-facility-header {
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto;
    gap: 7px;
    padding: 8px;
    background: #fff;
    border-color: #c9d9e8;
  }

  .cave-subpage-content >>> .farm-facility-title h3 {
    font-size: 16px;
  }

  .cave-subpage-content >>> .farm-facility-stats {
    grid-column: 1 / -1;
    width: auto;
    padding-left: 0;
    gap: 10px;
  }

  .cave-subpage-content >>> .farm-facility-layout,
  .cave-subpage-content >>> .forge-blueprint-layout,
  .cave-subpage-content >>> .forge-equipment-layout,
  .cave-subpage-content >>> .alchemy-workspace {
    grid-template-columns: 1fr;
  }

  .cave-subpage-content >>> .farm-facility-tabs,
  .cave-subpage-content >>> .farm-section-list {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .cave-subpage-content >>> .farm-list-toolbar {
    grid-template-columns: 1fr;
  }

  .cave-subpage-content >>> .farm-market-panel,
  .cave-subpage-content >>> .forge-panel,
  .cave-subpage-content >>> .farm-facility-panel,
  .cave-subpage-content >>> .farm-recipe-detail,
  .cave-subpage-content >>> .forge-detail,
  .cave-subpage-content >>> .forge-item-list {
    padding: 8px;
    background: #fff;
    border-color: #c9d9e8;
    box-shadow: none;
  }

  .cave-subpage-content >>> .farm-market-list,
  .cave-subpage-content >>> .forge-item-list {
    max-height: none;
  }

  .cave-subpage-content >>> .farm-market-row,
  .cave-subpage-content >>> .farm-queue-row,
  .cave-subpage-content >>> .forge-item-row,
  .cave-subpage-content >>> .farm-recipe-row {
    min-width: 0;
    padding: 8px;
  }

  .cave-subpage-content >>> .farm-market-row {
    align-items: flex-start;
  }

  .cave-subpage-content >>> .farm-market-row-actions,
  .cave-subpage-content >>> .farm-action-footer,
  .cave-subpage-content >>> .farm-queue-row > .ivu-btn {
    width: 100%;
  }

  .cave-subpage-content >>> .farm-market-row-actions .ivu-btn,
  .cave-subpage-content >>> .farm-action-footer .ivu-btn {
    flex: 1;
  }

  .cave-subpage-content >>> .farm-recipe-detail,
  .cave-subpage-content >>> .forge-detail {
    min-height: 0;
  }
}
</style>

<style>
/* Modal 会挂载到 body，使用独立全局类保证升级弹窗按钮也能切换主题。 */
.cave-upgrade-modal-blue .ivu-modal-footer .ivu-btn {
  color: #426581;
  background: #f7fbff;
  border-color: #b9cde0;
  box-shadow: none;
}
.cave-upgrade-modal-blue .ivu-modal-footer .ivu-btn:hover {
  color: #315a7c;
  background: #eef6fd;
  border-color: #7fa9ca;
}
.cave-upgrade-modal-blue .ivu-modal-footer .ivu-btn-primary {
  color: #fff;
  background: #3978b7;
  border-color: #2f669d;
}
.cave-upgrade-modal-blue .ivu-modal-footer .ivu-btn-primary:hover {
  color: #fff;
  background: #2f669d;
  border-color: #285783;
}
</style>



// WEBPACK FOOTER //
// src/components/CaveFarmSystem.vue